import {
  applySourceAdjust,
  createDitherGpuBackend,
  createGpuDiagnosticsPayload,
  dither,
  toGrayscale,
} from "@amitone/dither-core";
import type {
  DitherGpuBackend,
  DitherInput,
  DitherOptions,
  GpuCaps,
  GpuDiagnosticsInput,
  GpuDiagnosticsPayload,
  GpuRenderDiagnostics,
  MethodParams,
  StampShape,
  WebGL2BayerRegionBatchRegion,
  WebGL2FamilyAOrderedMethod,
  WebGL2FamilyBPatternMethod,
  WebGL2FamilyCTransformMethod,
  createDitherGpuBackend as CreateDitherGpuBackend,
  WebGL2DitherBackend as WebGL2DitherBackendClass,
} from "@amitone/dither-core";
import * as i18n from "./i18n";
import { registerServiceWorker } from "./pwa";
import {
  Store,
  buildMethod,
  buildMethodAtScale,
  buildRegions,
  buildRegionsAtScale,
  createResetSegmentationPatch,
  effectiveMethodFamily,
  effectiveDialValues,
  initialState,
} from "./state";
import type { DemoState, PatternAssetId, StampMaskAssetId } from "./state";
import { reflood } from "./util/region-ops";
import {
  pushUndoSnapshot,
} from "./util/region-undo";
import {
  canRedoCanonicalHistory,
  canUndoCanonicalHistory,
  cloneCanonicalSnapshot,
  createCanonicalHistory,
  redoCanonicalHistory,
  undoCanonicalHistory,
  pushCanonicalSnapshot,
} from "./util/canonical-history";
import { createCanvasPair } from "./ui/canvas-pair";
import { createAreaMaskSection, createControls } from "./ui/controls";
import {
  compositeReferenceOverlay,
  hasReferenceOverlayDimensionMismatch,
  compositeTransparentPixelsOverWhite,
  nearestUpscale,
} from "./ui/image-data";
import { createStatusBar, statusGpuModeFromState } from "./ui/status-bar";
import { bindCanvasViewMode, ViewModeStore } from "./ui/view-mode";
import { ZoomStore } from "./ui/zoom-state";
// zoom-toolbar deleted (Item 10b) - region outline now uses the F4 auto-overlay.
import { buildLabelmapPatch } from "./util/labelmap-import";
import { resolveAccentRgb } from "./util/accent-rgb";
import { computeRegionBoundary } from "./util/region-outline";
import { applyTheme, readSavedTheme } from "./ui/settings-popup";
import { createTopBar } from "./ui/top-bar";
import { isIpadLike } from "./util/ipad-detection";
import { createSidebarTabs } from "./ui/sidebar-tabs";
import { createToneThumbStrip } from "./ui/tone-thumb-strip";
import { createRegionList } from "./ui/region-list";
import { installKeybindings } from "./ui/keybindings";
import { createReferenceOverlayGroup } from "./ui/reference-overlay";
import {
  buildAdjustedSource,
  shouldRebuildAdjustedSourceForPatch,
} from "./util/source-adjust-pipeline";
import { renderDebounceMsForSource } from "./util/render-scheduler";
import { createHiddenReleaseScheduler } from "./util/visibility-release";
import { recoverSourceState as recoverSourceStateFallback } from "./util/recover-source";
import { createTemplateStackPanel } from "./ui/template-stack";
import {
  applyMethodPartial,
  buildMethodSnapshot,
  type MethodPartial,
} from "./util/method-patch";
import { registerAsset } from "./util/asset-registry";
import { bindTemplateGroupsForImport } from "./util/template-binding";
import {
  decodePresetBundle,
  decodeRegionTemplateBundle,
  decodeProjectBundle,
  encodeRegionTemplateBundleArchive,
  encodeProjectBundleArchive,
  type DecodedProjectBundle,
  type EmbeddedAssetInput,
} from "./persistence/bundles";
import {
  binaryPatchForMethodSnapshot,
  downloadPresetAsBundle,
  embeddedAssetsForMethodSnapshot,
  mergeEmbeddedAssetsIntoState,
  methodSnapshotToPresetPatch,
  validateImportedPreset,
} from "./ui/user-presets";

declare global {
  interface Window {
    __amitoneB0Probe?: {
      WebGL2DitherBackend: typeof WebGL2DitherBackendClass;
      createDitherGpuBackend: typeof CreateDitherGpuBackend;
      runSolidRedProof: () => Promise<{
        diagnostics: GpuRenderDiagnostics;
        firstPixelBytes: number[];
        width: number;
        height: number;
      }>;
      runOrientationProof: () => Promise<{
        diagnostics: GpuRenderDiagnostics;
        readbackBytes: number[];
        width: number;
        height: number;
      }>;
      runCellAverageProof: (source: ImageData, cellSize: number) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        avgBytes: number[];
        avgWidth: number;
        avgHeight: number;
        sourceWidth: number;
        sourceHeight: number;
        cellSize: number;
      }>;
      runFamilyABayerPixelProof: (
        source: ImageData,
        params: {
          cellSize: number;
          matrixSize: 2 | 4 | 8;
          polarity: "black" | "white";
          density: number;
        },
      ) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        width: number;
        height: number;
      }>;
      runFamilyAOrderedProof: (
        source: ImageData,
        method: WebGL2FamilyAOrderedMethod,
      ) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        width: number;
        height: number;
      }>;
      runFamilyBPatternProof: (
        source: ImageData,
        method: WebGL2FamilyBPatternMethod,
      ) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        cpuBytes: number[];
        width: number;
        height: number;
      }>;
      runFamilyCTransformProof: (
        source: ImageData,
        method: WebGL2FamilyCTransformMethod,
      ) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        cpuBytes: number[];
        width: number;
        height: number;
      }>;
      runFamilyCTransformMetrics: (
        source: ImageData,
        method: WebGL2FamilyCTransformMethod,
      ) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        width: number;
        height: number;
        diffPixels: number;
        flipRate: number;
        maxChannelDelta: number;
        gpuBlackPixels: number;
        cpuBlackPixels: number;
        gpuGrayPixels: number;
        cpuGrayPixels: number;
      }>;
      runDemoPipelineProof: () => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        width: number;
        height: number;
        textureBytes: number;
        heapBefore: number | null;
        heapAfter: number | null;
        heapDelta: number | null;
        sourceChecksumBefore: number;
        sourceChecksumAfter: number;
      }>;
      runRegionBatchProof: () => Promise<{
        diagnostics: GpuRenderDiagnostics;
        outputBytes: number[];
        width: number;
        height: number;
        textureBytes: number;
      }>;
      runPerRegionReadbackBenchmarkProof: (scenario?: {
        width?: number;
        height?: number;
        regionCount?: number;
      }) => Promise<{
        diagnostics: GpuRenderDiagnostics;
        perRegionReadbackMs: number[];
        width: number;
        height: number;
        regionCount: number;
      }>;
      runB8ValidationProof: (scenario?: {
        repeatedCycles?: number;
        largeFixtures?: Array<{ name: string; width: number; height: number }>;
      }) => Promise<{
        userAgent: string;
        platform: string;
        maxTouchPoints: number;
        isIpadLike: boolean;
        caps: GpuCaps | null;
        diagnosticsPayload: GpuDiagnosticsPayload;
        diagnosticsJson: string;
        contextLoss: {
          extensionAvailable: boolean;
          beforeReady: boolean;
          afterReady: boolean;
          contextLost: boolean;
          contextLossEvents: number;
          fallbackDiagnostics: GpuRenderDiagnostics | null;
          outputChecksum: number;
          error: string | null;
        };
        repeatedRender: {
          cycles: number;
          gpuFrames: number;
          fallbackFrames: number;
          readbackCounts: number[];
          totalMs: number;
          textureBytesMax: number;
          sourceChecksumBefore: number;
          sourceChecksumAfter: number;
        };
        visibilityCycle: {
          dispatchCount: number;
          diagnostics: GpuRenderDiagnostics | null;
          outputChecksum: number;
        };
        largeFixtures: Array<{
          name: string;
          width: number;
          height: number;
          diagnostics: GpuRenderDiagnostics;
          outputChecksum: number;
          totalMs: number;
          textureBytes: number;
        }>;
      }>;
      runB9FixtureProof: (scenario: {
        name: string;
        width: number;
        height: number;
        sourceKind:
          | "flat"
          | "horizontal-gradient"
          | "vertical-gradient"
          | "line-art"
          | "hair-lines"
          | "speech-edge"
          | "manga-page";
        tone?: number;
        family: "A" | "B" | "C";
        algorithm?: "bayer" | "bluenoise";
        cellSize?: number;
        stamp?: StampShape["type"];
        labelMode?: "none" | "one" | "three" | "four" | "eight";
        exact?: boolean;
      }) => Promise<{
        name: string;
        width: number;
        height: number;
        family: "A" | "B" | "C";
        backend: GpuRenderDiagnostics["backend"] | "missing";
        diagnostics: GpuRenderDiagnostics | null;
        metrics: {
          totalPixels: number;
          flippedPixels: number;
          flipRate: number;
          maxFlipClusterArea: number;
          localDensityMaxDelta16: number;
          localDensityMaxDelta32: number;
          seamBandFlipRate: number;
        };
        cpuMs: number;
        gpuMs: number;
        contextLossCount: number;
        cpuPng: string;
        gpuPng: string;
        diffPng: string;
      }>;
    };
  }
}

// Phase 0: error-diffusion algorithms run on the main thread. The
// dither-core's worker path is fully implemented and tested but the
// `?worker&url` import didn't resolve cleanly across the workspace boundary
// at build time. With cellSize >= 2 (the demo default is 4) FS / Atkinson /
// Werness are fast enough to keep the UI responsive.

let renderToken = 0;
let exportRenderToken = 0;

/**
 * Derive a human-readable method label from the effective family/algorithm
 * for the current active selection (active stack → active region → global).
 *
 * Uses `effectiveMethodFamily` so the status bar stays in sync with the
 * controls panel, which already reads from the same priority order.
 */
function methodLabelFromState(state: DemoState): string {
  const { family, algorithmA } = effectiveMethodFamily(state);
  if (family === "A") {
    return i18n.t("method.family.a");
  }
  if (family === "B") {
    return i18n.t("algorithm.custom-pattern");
  }
  return i18n.t(`algorithm.${algorithmA}`);
}

function statusBaseFor(
  state: DemoState,
): Pick<
  Parameters<ReturnType<typeof createStatusBar>["update"]>[0],
  "methodLabel" | "messageKey"
> {
  return {
    methodLabel: state.source ? methodLabelFromState(state) : null,
    messageKey: state.labelmapError,
  };
}

function dilateMask(
  mask: Uint8Array,
  width: number,
  height: number,
): Uint8Array {
  const next = new Uint8Array(mask);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (mask[idx] !== 1) continue;
      if (x > 0) next[idx - 1] = 1;
      if (x < width - 1) next[idx + 1] = 1;
      if (y > 0) next[idx - width] = 1;
      if (y < height - 1) next[idx + width] = 1;
    }
  }
  return next;
}

function clampOverlayAlpha(value: number): number {
  if (!Number.isFinite(value)) return 76;
  return Math.min(255, Math.max(0, Math.round(value)));
}

function clampOverlayBorderWidth(value: number): number {
  if (!Number.isFinite(value)) return 2;
  return Math.min(8, Math.max(1, Math.round(value)));
}

async function main(): Promise<void> {
  // Theme bootstrap is the very first thing - applies before first paint.
  applyTheme(readSavedTheme());

  registerServiceWorker();
  await i18n.init();
  document.documentElement.lang = i18n.currentLocale();
  // Item 13 - i18n-driven document title. Re-applied on every locale change.
  function syncDocumentTitle(): void {
    document.title = i18n.t("app.name");
  }
  syncDocumentTitle();

  i18n.onLocaleChange(() => {
    document.documentElement.lang = i18n.currentLocale();
    syncDocumentTitle();
  });

  const root = document.getElementById("app");
  if (!root) throw new Error("#app root missing");
  root.replaceChildren();

  // ========== TOP BAR (consolidated Phase 2 layout) ==========
  //
  // Slot layout (left → right):
  //   1. Brand mark (A glyph, no-op click)
  //   2. Load image  - file picker for source image
  //   3. Load mask   - file picker for labelmap PNG
  //   4. Clear       - wired to clearImage()
  //   5. Fit         - wired to zoomStore.fit (deferred until zoomStore exists)
  //   6. 1:1         - wired to zoomStore.set zoom=1 (deferred)
  //   7. Result / Original toggle - wired to ViewModeStore (deferred)
  //   8. Settings gear - opens settings popup
  //
  // Fit/1:1/toggle button handlers are wired after zoomStore/viewMode are
  // constructed below (they are passed via deferred callbacks).

  // Tracks the zoom value that corresponds to the "Fit" preset so the subscribe
  // block can detect when scroll departs from it (Item 2 fix).
  let lastFitZoom = 1;
  let gpuBackend: DitherGpuBackend | null = null;
  let gpuCaps: GpuCaps | null = null;
  let gpuContextLossCount = 0;
  let gpuLastContextLossTimestamp: number | null = null;
  let gpuLastError: string | null = null;

  function serialiseGpuError(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
  }

  function webgl2Available(): boolean {
    try {
      return document.createElement("canvas").getContext("webgl2") !== null;
    } catch {
      return false;
    }
  }

  function labelsForDitherInput(input: DitherInput): number[] {
    return input.regions.map((region) => region.label);
  }

  function cpuDiagnosticsForInput(
    input: DitherInput,
    gpuError: string | null = null,
  ): GpuRenderDiagnostics {
    const fallbackLabels = labelsForDitherInput(input);
    if (gpuError) {
      return {
        backend: "fallback-cpu-after-gpu-error",
        attemptedBackend: "gpu-webgl2",
        gpuError,
        readbackCount: 0,
        renderedLabels: [],
        fallbackLabels,
      };
    }
    return {
      backend: "cpu",
      readbackCount: 0,
      renderedLabels: [],
      fallbackLabels,
    };
  }

  function disposeGpuBackend(): void {
    gpuBackend?.dispose();
    gpuBackend = null;
    gpuCaps = null;
  }

  async function ensureGpuBackend(): Promise<DitherGpuBackend | null> {
    const state = store.get();
    if (!state.gpuEnabled) {
      disposeGpuBackend();
      if (state.gpuEnabledForSession || state.lastGpuDiagnostics !== null) {
        store.patch({
          gpuEnabledForSession: false,
          lastGpuDiagnostics: null,
        });
      }
      return null;
    }

    if (gpuBackend?.isReady()) return gpuBackend;

    disposeGpuBackend();
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      gpuContextLossCount += 1;
      gpuLastContextLossTimestamp = Date.now();
      store.patch({ gpuEnabledForSession: false });
    });

    try {
      const backend = await createDitherGpuBackend(
        { kind: "webgl2", canvas },
        { preferred: "webgl2", allowFallback: false },
      );
      if (!backend) {
        throw new Error("createDitherGpuBackend returned null");
      }
      gpuBackend = backend;
      gpuCaps = backend.getCaps();
      gpuLastError = null;
      if (!store.get().gpuEnabledForSession) {
        store.patch({ gpuEnabledForSession: true });
      }
      return backend;
    } catch (err) {
      const message = serialiseGpuError(err);
      gpuLastError = message;
      disposeGpuBackend();
      store.patch({
        gpuEnabledForSession: false,
        lastGpuDiagnostics: {
          backend: "fallback-cpu-after-gpu-error",
          attemptedBackend: "gpu-webgl2",
          gpuError: message,
          readbackCount: 0,
          renderedLabels: [],
          fallbackLabels: [],
        },
      });
      return null;
    }
  }

  async function reinitialiseGpu(): Promise<void> {
    disposeGpuBackend();
    await ensureGpuBackend();
    scheduleRender();
  }

  function observeFrameBackend(
    backend: DitherGpuBackend,
    onDiagnostics: (diagnostics: GpuRenderDiagnostics) => void,
  ): DitherGpuBackend {
    return {
      name: backend.name,
      initialize: () => backend.initialize(),
      isReady: () => backend.isReady(),
      getCaps: () => {
        gpuCaps = backend.getCaps();
        return gpuCaps;
      },
      renderRegionBatch: async (req) => {
        const result = await backend.renderRegionBatch(req);
        onDiagnostics(result.diagnostics);
        return result;
      },
      dispose: () => backend.dispose(),
    };
  }

  // Build the top bar via the extracted factory. Callbacks reference vars
  // that don't exist yet (store, canvases, zoomStore, viewMode) so we use
  // arrow functions that close over them - by the time any button is clicked
  // the full application is initialised.
  let includeReferenceOverlayInExport = true;
  const topBarHandles = createTopBar(
    i18n,
    {
      onFileChange: (file) => void loadImage(file),
      onMaskChange: (file) => void handleMaskFile(file),
      onMaskClear: () => clearMaskOnly(),
      onProjectFileChange: (file) => void loadProjectFile(file),
      onProjectSave: () => saveProjectFile(),
      onTemplateFileChange: (file) => void importRegionTemplateFile(file),
      onTemplateSave: () => exportRegionTemplateFile(),
      onPresetFileChange: (file) => void importPresetFile(file),
      onPresetExport: () => exportCurrentPresetFile(),
      onOverlayFileChange: (file) => void applyReferenceOverlayFile(file),
      onOverlayClear: () => clearReferenceOverlay(),
      getIncludeReferenceOverlay: () => includeReferenceOverlayInExport,
      onIncludeReferenceOverlayChange: (enabled) => {
        includeReferenceOverlayInExport = enabled;
      },
      onExportScaleChange: (scale) => {
        controlsHandles.exportScaleSelect.value = scale;
      },
      onExport: () => controlsHandles.exportButton.click(),
      onCopy: () => controlsHandles.copyButton.click(),
      onUndo: () => undoCanonicalState(),
      onRedo: () => redoCanonicalState(),
      onClear: () => clearImage(),
      onFit: () => {
        zoomStore.fit(
          {
            w: store.get().source?.width ?? 0,
            h: store.get().source?.height ?? 0,
          },
          {
            w: canvases.viewport.clientWidth,
            h: canvases.viewport.clientHeight,
          },
        );
        // Record the resulting zoom so the subscribe block can match it.
        lastFitZoom = zoomStore.get().zoom;
        topBarHandles.syncZoomMode("fit");
      },
      onOneToOne: () => {
        const vp = {
          w: canvases.viewport.clientWidth,
          h: canvases.viewport.clientHeight,
        };
        const src = store.get().source;
        zoomStore.set({
          zoom: 1,
          panX: (vp.w - (src?.width ?? 0)) / 2,
          panY: (vp.h - (src?.height ?? 0)) / 2,
        });
        topBarHandles.syncZoomMode("1:1");
      },
      onTwoHundred: () => {
        const vp = {
          w: canvases.viewport.clientWidth,
          h: canvases.viewport.clientHeight,
        };
        const src = store.get().source;
        zoomStore.set({
          zoom: 2,
          panX: (vp.w - (src?.width ?? 0) * 2) / 2,
          panY: (vp.h - (src?.height ?? 0) * 2) / 2,
        });
        topBarHandles.syncZoomMode("200%");
      },
      onZoomChange: (percent) => {
        const vp = {
          w: canvases.viewport.clientWidth,
          h: canvases.viewport.clientHeight,
        };
        zoomStore.zoomToward(
          { x: vp.w / 2, y: vp.h / 2 },
          percent / 100 / zoomStore.get().zoom,
        );
        topBarHandles.syncZoomMode("custom");
      },
      onViewToggle: () => {
        const current = viewMode.get();
        viewMode.set(current === "result" ? "source" : "result");
      },
      getSource: () => store.get().source,
    },
    {
      // Wire the live Store + iPad detection into the settings popup so the
      // GPU toggle and sticky diagnostic indicator read and write the runtime
      // state rather than the popup's fallback snapshot. Without this the
      // toggle has no effect on the running app (Sonnet review caught this on
      // the v0.2.5.1 B6 PR).
      //
      // `store` is declared later in this function (TDZ), so the read/write
      // closures defer access through try/catch. createTopBar runs the popup's
      // initial syncGpuToggle synchronously, which triggers getGpuState before
      // `store` exists. The catch path returns the same default the popup
      // would have computed from fallbackGpuState() anyway.
      getGpuState: () => {
        try {
          const s = store.get();
          return {
            gpuEnabled: s.gpuEnabled,
            gpuEnabledForSession: s.gpuEnabledForSession,
            lastGpuDiagnostics: s.lastGpuDiagnostics,
          };
        } catch {
          return {
            gpuEnabled: initialState.gpuEnabled,
            gpuEnabledForSession: initialState.gpuEnabledForSession,
            lastGpuDiagnostics: initialState.lastGpuDiagnostics,
          };
        }
      },
      patchGpuState: (patch) => {
        try {
          store.patch(patch);
        } catch {
          // Store not yet initialised; the popup's initial sync runs once more
          // after main finishes, so this is recoverable.
        }
      },
      isIpadLike,
      webgl2Available,
      reinitialiseGpu: () => {
        void reinitialiseGpu();
      },
      reload: () => {
        // Indirected via a tiny shim so tests can stub it.
        window.location.reload();
      },
      // Wire the live Store into the diagnostics accessor so the About popup
      // reports real gpuEnabled / gpuEnabledForSession values. The renderer
      // pipeline (B1) is not yet wired, so caps / contextLoss / capability set
      // default to their safe zero-state. The same TDZ guard as getGpuState
      // applies: if store is not yet initialised the catch path returns a
      // no-renderer snapshot that matches the fallback the popup would have used.
      getGpuDiagnosticsInput: (): GpuDiagnosticsInput => {
        try {
          const s = store.get();
          return {
            userAgent: navigator.userAgent,
            isIpadLike: isIpadLike(),
            gpuEnabled: s.gpuEnabled,
            available: s.gpuEnabledForSession,
            caps: gpuCaps,
            contextLoss: {
              contextLossCount: gpuContextLossCount,
              lastContextLossTimestamp: gpuLastContextLossTimestamp,
            },
            currentCapabilitySet: gpuCaps ? ["webgl2"] : [],
            lastShaderCompileError: gpuLastError,
          };
        } catch {
          return {
            userAgent: navigator.userAgent,
            isIpadLike: isIpadLike(),
            gpuEnabled: initialState.gpuEnabled,
            available: false,
            caps: null,
            contextLoss: {
              contextLossCount: 0,
              lastContextLossTimestamp: null,
            },
            currentCapabilitySet: [],
            lastShaderCompileError: null,
          };
        }
      },
    },
  );

  const {
    element: topbar,
    fileInput,
    maskInput,
    clearButton,
    syncLoadMaskEnabled,
    syncViewToggleLabel,
    syncZoomDisplay,
    syncZoomMode,
  } = topBarHandles;

  root.appendChild(topbar);

  // ========== LAYOUT (sidebar + center) ==========
  const layout = document.createElement("main");
  layout.className = "layout";

  const store = new Store(initialState);
  const zoomStore = new ZoomStore();
  let lastReferenceOverlayMismatch: string | null = null;
  let canonicalHistory = createCanonicalHistory(store.get());
  let previousCanonicalState = cloneCanonicalSnapshot(store.get());
  let isRestoringCanonicalHistory = false;

  function syncHistoryButtons(): void {
    topBarHandles.syncHistoryButtons({
      canUndo: canUndoCanonicalHistory(canonicalHistory),
      canRedo: canRedoCanonicalHistory(canonicalHistory),
    });
  }

  function isCanonicalPatch(patch: Partial<DemoState>): boolean {
    const ignored = new Set<keyof DemoState>([
      "uiActiveTab",
      "uiRegionGridColumns",
      "showRegionOutline",
      "refloodMode",
      "regionUndoStack",
      "lastGpuDiagnostics",
      "gpuEnabled",
      "gpuEnabledForSession",
    ]);
    return (Object.keys(patch) as (keyof DemoState)[]).some(
      (key) => !ignored.has(key),
    );
  }

  store.subscribe((state, patch) => {
    if (!isRestoringCanonicalHistory && isCanonicalPatch(patch)) {
      canonicalHistory = pushCanonicalSnapshot(
        canonicalHistory,
        previousCanonicalState,
      );
    }
    previousCanonicalState = cloneCanonicalSnapshot(state);
    syncHistoryButtons();
  });
  syncHistoryButtons();

  // Install global region keybindings (Cmd+G = create stack, Shift+Cmd+G = disband)
  // and zoom keybindings (+/- for zoom in/out, D2).
  installKeybindings(store, zoomStore);

  // ── Sidebar (Phase 3 two-panel layout) ──────────────────────────────────────
  //
  // The sidebar `.controls` element is constructed here but its contents are
  // filled by the two tab panels:
  //   Region panel: region list + ops toolbar + replace-mask button.
  //   Tone panel:   thumbnail strip + tone controls.
  //
  // `createControls` renders into the Tone panel container.

  // Sidebar container (renamed from .controls to avoid collision with the
  // inner controls aside created by createControls()).
  const sidebarEl = document.createElement("div");
  sidebarEl.className = "sidebar";

  // ── Hamburger toggle for mobile drawer ──────────────────────────────────
  const hamburgerBtn = document.createElement("button");
  hamburgerBtn.type = "button";
  hamburgerBtn.className = "sidebar-hamburger top-bar-btn";
  hamburgerBtn.setAttribute("aria-label", i18n.t("aria.sidebar.toggle"));
  hamburgerBtn.title = i18n.t("aria.sidebar.toggle");
  hamburgerBtn.setAttribute("aria-expanded", "false");
  hamburgerBtn.textContent = "☰";
  function syncSidebarDrawerEnabled(hasImage: boolean): void {
    hamburgerBtn.disabled = !hasImage;
    if (hasImage) {
      hamburgerBtn.removeAttribute("aria-disabled");
      return;
    }
    hamburgerBtn.setAttribute("aria-disabled", "true");
    document.body.classList.remove("sidebar-open");
    hamburgerBtn.setAttribute("aria-expanded", "false");
  }
  syncSidebarDrawerEnabled(false);
  hamburgerBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    if (hamburgerBtn.disabled) return;
    const isOpen = document.body.classList.toggle("sidebar-open");
    hamburgerBtn.setAttribute("aria-expanded", String(isOpen));
  });
  // Keep the sidebar drawer trigger reachable on narrow phones where the
  // top bar scrolls horizontally.
  {
    const tb = topBarHandles.element;
    const brandGroup = tb.querySelector(".top-bar-group-brand");
    if (brandGroup?.nextSibling) {
      tb.insertBefore(hamburgerBtn, brandGroup.nextSibling);
    } else {
      tb.appendChild(hamburgerBtn);
    }
  }
  i18n.onLocaleChange(() => {
    hamburgerBtn.setAttribute("aria-label", i18n.t("aria.sidebar.toggle"));
    hamburgerBtn.title = i18n.t("aria.sidebar.toggle");
  });

  // Close sidebar when clicking outside it on mobile.
  document.addEventListener("click", (e) => {
    if (
      document.body.classList.contains("sidebar-open") &&
      !sidebarEl.contains(e.target as Node) &&
      !hamburgerBtn.contains(e.target as Node)
    ) {
      document.body.classList.remove("sidebar-open");
      hamburgerBtn.setAttribute("aria-expanded", "false");
    }
  });

  // ── Build Region panel container ─────────────────────────────────────────
  const regionPanel = document.createElement("div");
  regionPanel.className = "sidebar-region-panel";

  // B3 - empty placeholder (no image loaded).
  const regionEmptyEl = document.createElement("div");
  regionEmptyEl.className = "region-empty";
  regionEmptyEl.dataset["i18n"] = "region.empty";
  regionEmptyEl.textContent = i18n.t("region.empty");
  regionPanel.appendChild(regionEmptyEl);

  // Inner wrapper: AREA MASK appears once a source exists; the region list is
  // gated separately until both source and mask are present.
  const regionListAndMaskContent = document.createElement("div");
  regionListAndMaskContent.className = "region-panel-content";

  // B1 - AREA MASK section rendered into region panel (moved from Tone tab).
  const areaMaskHandles = createAreaMaskSection(
    regionListAndMaskContent,
    store,
    {
      onOpenMaskPicker: () => topBarHandles.openMaskPicker(),
      onMaskFile: (file) => void handleMaskFile(file),
      onDeleteMask: () => {
        // Clear the labelmap and all region state. The confirm dialog lives
        // in createAreaMaskSection so this callback only fires after approval.
        store.patch(createResetSegmentationPatch());
        areaMaskHandles.setLabelmapStatus(null);
      },
    },
  );

  // Region list.
  const regionListRoot = createRegionList(regionListAndMaskContent, store);

  regionPanel.appendChild(regionListAndMaskContent);

  // B3 - locale updates for region panel strings.
  i18n.onLocaleChange(() => {
    regionEmptyEl.textContent = i18n.t("region.empty");
  });

  /** Update the 3-state visibility of the region panel.
   *
   * States:
   *   - No source, no mask: empty placeholder.
   *   - Source OR mask only: AREA MASK with Load mask; region list hidden.
   *   - Source AND mask: AREA MASK with Replace mask + region list.
   */
  function syncRegionPanelState(state: DemoState): void {
    const hasSource = state.source !== null;
    const hasMask = state.labelMap !== null;

    // Empty placeholder: only visible when no source is loaded.
    regionEmptyEl.hidden = hasSource;

    // AREA MASK is visible once a source exists so the mask loader lives in
    // its final section. The region grid stays hidden until a mask is loaded.
    regionListAndMaskContent.hidden = !(hasSource || hasMask);
    regionListRoot.hidden = !(hasSource && hasMask);
  }

  // Initial state sync.
  syncRegionPanelState(store.get());

  // Subscribe for future state changes.
  store.subscribe((state, patch) => {
    if ("source" in patch || "labelMap" in patch) {
      syncRegionPanelState(state);
    }
  });

  // ── Build Tone panel container ────────────────────────────────────────────
  const tonePanel = document.createElement("div");
  tonePanel.className = "sidebar-tone-panel";

  // Thumbnail strip at the top of the Tone panel.
  createToneThumbStrip({ parent: tonePanel, store, i18n });

  // Controls panel - returns Export/Copy buttons that live inside section 04.
  // Rendered into the Tone panel (tone controls).
  const controlsHandles = createControls(tonePanel, store);

  // ── Build Template Stack panel container ─────────────────────────────────
  const templatePanel = document.createElement("div");
  templatePanel.className = "sidebar-template-panel";
  createTemplateStackPanel({
    parent: templatePanel,
    i18n,
    store,
    callbacks: {
      onExport: () => exportRegionTemplateFile(),
      onImportFile: (file) => void importRegionTemplateFile(file),
      onRebind: (templateRegionId, regionUuid) =>
        rebindTemplateRegion(templateRegionId, regionUuid),
    },
  });
  createReferenceOverlayGroup(
    topBarHandles.referenceOverlayMount,
    {
      store,
      loadOverlayImage,
      toast: showToast,
    },
    {
      visibilityParent: topBarHandles.referenceOverlayVisibilityMount,
      triggerVariant: "file-menu",
    },
  );

  // ── Wrap the two panels in the sidebar tab shell ──────────────────────────
  createSidebarTabs({
    parent: sidebarEl,
    i18n,
    store,
    regionPanel,
    tonePanel,
    templatePanel,
  });

  layout.appendChild(sidebarEl);

  const center = document.createElement("div");
  center.className = "center";
  const viewMode = new ViewModeStore();
  const canvases = createCanvasPair(center, zoomStore, viewMode, layout);
  bindCanvasViewMode(canvases.source, canvases.result, viewMode);
  // zoom-toolbar removed (Item 10b) - region outline now uses the F4
  // auto-overlay; keyboard hints moved to the footer (Item 10c).
  // ========== re-flood canvas click handler (Item 27) ==========
  //
  // When refloodMode is active the result canvas cursor changes to crosshair
  // and a click triggers reflood at the clicked label-plane coordinate.
  //
  // Coordinate conversion: the canvas element occupies its natural pixel size
  // (canvas.width × canvas.height) but is CSS-scaled by zoom+pan via the
  // stack transform. getBoundingClientRect() returns the scaled on-screen size,
  // so dividing by the ratio (screenWidth / pixelWidth) converts back to
  // label-plane pixels.

  function syncRefloodCursor(refloodMode: boolean): void {
    canvases.result.style.cursor = refloodMode ? "crosshair" : "";
  }
  syncRefloodCursor(false);

  canvases.result.addEventListener("click", (event: MouseEvent) => {
    const state = store.get();
    if (!state.refloodMode) return;
    if (state.regionLabelPlane.length === 0 || !state.source) return;

    // Convert click position to canvas pixel space.
    const rect = canvases.result.getBoundingClientRect();
    const scaleX = canvases.result.width / rect.width;
    const scaleY = canvases.result.height / rect.height;
    const labelX = (event.clientX - rect.left) * scaleX;
    const labelY = (event.clientY - rect.top) * scaleY;

    const patch = reflood(
      {
        regionEntries: state.regionEntries,
        regionLabelPlane: state.regionLabelPlane,
        activeRegionUuids: state.activeRegionUuids,
        width: state.source.width,
        height: state.source.height,
        labelMap: state.labelMap,
        regions: state.regions,
      },
      { x: labelX, y: labelY },
      (label) => i18n.t("label.regionN", { n: label }),
    );

    // Only push undo snapshot when reflood succeeded (status 'ok').
    // No-op / cap / oob / unlabelled results leave the state unchanged.
    if (patch.status === "ok") {
      const undoStack = pushUndoSnapshot(state);
      store.patch({
        regionEntries: patch.regionEntries,
        regionLabelPlane: patch.regionLabelPlane,
        activeRegionUuids: patch.activeRegionUuids,
        labelMap: patch.labelMap,
        regions: patch.regions,
        regionUndoStack: undoStack,
      });
    }
  });

  layout.appendChild(center);

  root.appendChild(layout);

  // ========== Undo / redo keyboard shortcuts ==========
  //
  // Cmd/Ctrl+Z restores the previous canonical state. Cmd/Ctrl+Shift+Z and
  // Cmd/Ctrl+Y redo. The handler is a no-op when focus is on a text input or
  // contenteditable element so typing is never interrupted.

  document.addEventListener("keydown", (event: KeyboardEvent) => {
    const isUndo =
      (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === "z";
    const isRedo =
      ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === "z") ||
      ((event.metaKey || event.ctrlKey) && event.key === "y");
    if (!isUndo && !isRedo) return;

    // Do not intercept undo when the user is typing in an input or editable area.
    const target = event.target as HTMLElement | null;
    if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
    if (target?.isContentEditable) return;

    event.preventDefault();
    if (isRedo) {
      redoCanonicalState();
      return;
    }
    undoCanonicalState();
  });

  let gestureTap:
    | {
        touches: number;
        startedAt: number;
        points: Array<{ x: number; y: number }>;
        cancelled: boolean;
      }
    | null = null;

  canvases.viewport.addEventListener("touchstart", (event: TouchEvent) => {
    if (event.touches.length !== 2 && event.touches.length !== 3) {
      gestureTap = null;
      return;
    }
    gestureTap = {
      touches: event.touches.length,
      startedAt: performance.now(),
      points: Array.from(event.touches).map((touch) => ({
        x: touch.clientX,
        y: touch.clientY,
      })),
      cancelled: false,
    };
  });

  canvases.viewport.addEventListener("touchmove", (event: TouchEvent) => {
    if (!gestureTap) return;
    if (event.touches.length !== gestureTap.touches) {
      gestureTap.cancelled = true;
      return;
    }
    const moved = Array.from(event.touches).some((touch, index) => {
      const start = gestureTap?.points[index];
      if (!start) return true;
      return Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 18;
    });
    if (moved) gestureTap.cancelled = true;
  });

  canvases.viewport.addEventListener("touchend", () => {
    if (!gestureTap) return;
    const tap = gestureTap;
    gestureTap = null;
    if (tap.cancelled) return;
    if (performance.now() - tap.startedAt > 260) return;
    if (tap.touches === 2) {
      undoCanonicalState();
    } else if (tap.touches === 3) {
      redoCanonicalState();
    }
  });

  // ========== STATUS BAR ==========
  const footer = document.createElement("footer");
  footer.className = "status-footer";
  const statusBar = createStatusBar(footer);
  root.appendChild(footer);
  statusBar.updateGpuMode(statusGpuModeFromState(store.get()));
  store.subscribe((state, patch) => {
    if (
      "lastGpuDiagnostics" in patch ||
      "gpuEnabled" in patch ||
      "gpuEnabledForSession" in patch ||
      "source" in patch
    ) {
      statusBar.updateGpuMode(statusGpuModeFromState(state));
    }
  });
  store.subscribe((_state, patch) => {
    if (!("gpuEnabled" in patch)) return;
    if (patch.gpuEnabled) return;
    disposeGpuBackend();
    store.patch({
      gpuEnabledForSession: false,
      lastGpuDiagnostics: null,
    });
  });

  // ========== file loading ==========
  function firstImageFile(files: FileList): File | null {
    for (const item of Array.from(files)) {
      if (item.type.startsWith("image/")) return item;
    }
    return null;
  }

  function firstImageClipboardItem(items: DataTransferItemList): File | null {
    for (const item of Array.from(items)) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (file?.type.startsWith("image/")) return file;
    }
    return null;
  }

  let lastResult: ImageData | null = null;
  let lastResultToken = -1;
  let latestSettledRenderToken = -1;
  const renderWaiters = new Map<number, Array<() => void>>();

  function clearLastResult(): void {
    lastResult = null;
    lastResultToken = -1;
  }

  function waitForRenderToken(token: number): Promise<void> {
    if (lastResultToken === token || latestSettledRenderToken >= token) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const waiters = renderWaiters.get(token);
      if (waiters) {
        waiters.push(resolve);
        return;
      }
      renderWaiters.set(token, [resolve]);
    });
  }

  function notifyRenderSettled(token: number): void {
    latestSettledRenderToken = Math.max(latestSettledRenderToken, token);
    const waiters = renderWaiters.get(token);
    if (!waiters) return;
    renderWaiters.delete(token);
    for (const resolve of waiters) resolve();
  }

  /**
   * Draw the active-region highlight onto the dedicated overlay canvas.
   *
   * Export safety: `lastResult` stores the clean dither output and the overlay
   * canvas is never read by resultToPngBlob(), so exports never include the UI
   * highlight.
   *
   * Implementation note (perf): we build a single ImageData buffer in O(n)
   * over the labelPlane (one pass per active entry for the fill, plus the
   * boundary scan inside `computeRegionBoundary`) and blit it once with
   * `putImageData`. The previous fillRect-per-pixel approach was O(n) canvas
   * API calls per pixel, which caused observable hover lag on ~2000×2000
   * labelmaps. Colours are resolved from the live `--violet` / `--yellow`
   * CSS variables so canvas overlays cannot drift from the UI tokens.
   */
  function drawRegionOverlay(state: DemoState): void {
    const overlay = canvases.overlay;
    const ctx = overlay.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, overlay.width, overlay.height);
    overlay.style.visibility = "hidden";

    if (state.uiActiveTab !== "region") return;
    if (state.activeRegionUuids.length === 0) return;
    if (state.regionEntries.length === 0) return;
    const labelPlane = state.regionLabelPlane;
    if (labelPlane.length === 0) return;
    const source = state.source;
    if (!source) return;

    const width = source.width;
    const height = source.height;
    if (width <= 0 || height <= 0) return;
    if (labelPlane.length < width * height) return;

    const activeEntries = state.activeRegionUuids
      .map((uuid) => state.regionEntries.find((e) => e.uuid === uuid))
      .filter(
        (entry): entry is NonNullable<typeof entry> => entry !== undefined,
      );
    if (activeEntries.length === 0) return;

    overlay.style.visibility = "visible";

    const [vR, vG, vB] = resolveAccentRgb("--violet", document.documentElement);
    const [yR, yG, yB] = resolveAccentRgb("--yellow", document.documentElement);
    const violetAlpha = clampOverlayAlpha(state.regionOverlay.fillAlpha);
    const borderWidthPx = clampOverlayBorderWidth(
      state.regionOverlay.borderWidthPx,
    );

    const buffer = new ImageData(width, height);
    const data = buffer.data;

    // Pass 1: violet fill (about 50% alpha) for every pixel belonging to an active
    // entry. Skipping the early-out when none match is fine - the buffer is
    // zero-initialised.
    for (const entry of activeEntries) {
      const label = entry.label;
      for (let i = 0; i < labelPlane.length; i++) {
        if (labelPlane[i] !== label) continue;
        const o = i * 4;
        data[o] = vR;
        data[o + 1] = vG;
        data[o + 2] = vB;
        data[o + 3] = violetAlpha;
      }
    }

    // Pass 2: opaque yellow boundary. Overwrites the fill on boundary pixels.
    for (const entry of activeEntries) {
      let boundary = computeRegionBoundary(
        labelPlane,
        width,
        height,
        entry.label,
      );
      for (let pass = 1; pass < borderWidthPx; pass++) {
        boundary = dilateMask(boundary, width, height);
      }
      for (let i = 0; i < boundary.length; i++) {
        if (boundary[i] !== 1) continue;
        const o = i * 4;
        data[o] = yR;
        data[o + 1] = yG;
        data[o + 2] = yB;
        data[o + 3] = 255;
      }
    }

    ctx.putImageData(buffer, 0, 0);
  }

  function showToast(
    key: string,
    params?: Record<string, string | number>,
  ): void {
    const existing = document.body.querySelector<HTMLElement>(".app-toast");
    existing?.remove();
    const toast = document.createElement("div");
    toast.className = "app-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.textContent = i18n.t(key, params);
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), 3000);
  }

  async function loadOverlayImage(file: Blob): Promise<ImageData> {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  }

  async function applyReferenceOverlayFile(file: File): Promise<void> {
    const sourceAtStart = store.get().source;
    if (!sourceAtStart) return;
    try {
      const image = await loadOverlayImage(file);
      if (store.get().source !== sourceAtStart) return;
      const current = store.get().referenceOverlay;
      store.patch({
        referenceOverlay: {
          image,
          enabled: true,
          opacity: current.opacity,
        },
      });
    } catch {
      showToast("toast.referenceOverlay.decodeFail");
    }
  }

  function clearReferenceOverlay(): void {
    store.patch({
      referenceOverlay: { image: null, opacity: 1, enabled: true },
    });
  }

  function clearMaskOnly(): void {
    store.patch(createResetSegmentationPatch());
    areaMaskHandles.setLabelmapStatus(null);
  }

  function referenceOverlayForOutput(
    state: DemoState,
    base: ImageData,
    scale = 1,
    include = true,
  ): DemoState["referenceOverlay"] {
    if (!include) {
      return { ...state.referenceOverlay, image: null, enabled: false };
    }
    const overlay = state.referenceOverlay;
    const image =
      overlay.image && scale > 1
        ? nearestUpscale(overlay.image, scale)
        : overlay.image;
    if (image && overlay.enabled) {
      const mismatch = `${image.width}x${image.height}->${base.width}x${base.height}`;
      if (
        hasReferenceOverlayDimensionMismatch(base, image) &&
        lastReferenceOverlayMismatch !== mismatch
      ) {
        lastReferenceOverlayMismatch = mismatch;
        showToast("toast.referenceOverlay.dimensionMismatch");
      }
      if (!hasReferenceOverlayDimensionMismatch(base, image)) {
        lastReferenceOverlayMismatch = null;
      }
    }
    return { ...overlay, image };
  }

  async function loadImage(file: File): Promise<void> {
    const bitmap = await createImageBitmap(file);
    canvases.source.width = bitmap.width;
    canvases.source.height = bitmap.height;
    canvases.result.width = bitmap.width;
    canvases.result.height = bitmap.height;
    canvases.overlay.width = bitmap.width;
    canvases.overlay.height = bitmap.height;
    canvases.frame.style.width = `${bitmap.width}px`;
    canvases.frame.style.height = `${bitmap.height}px`;
    canvases.stack.style.width = `${bitmap.width}px`;
    canvases.stack.style.height = `${bitmap.height}px`;
    const ctx = canvases.source.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.drawImage(bitmap, 0, 0);
    const colored = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const gray = toGrayscale(compositeTransparentPixelsOverWhite(colored));
    ctx.putImageData(gray, 0, 0);
    canvases.setHasImage(true);
    clearLastResult();
    setOutputActionsEnabled(false);

    clearButton.disabled = false;
    clearButton.removeAttribute("aria-disabled");
    syncLoadMaskEnabled(true);
    // R10 Yuko spec: before image loads, only Load image + Settings gear
    // are interactive. Enable Fit / 100% / zoom cluster / view toggle now.
    topBarHandles.syncImageGated(true);
    syncSidebarDrawerEnabled(true);

    // Store both the original (immutable across source-adjust slider
    // changes) and the adjusted source the dither pipeline reads.
    // applySourceAdjust at default values is an identity copy so this
    // adds no measurable cost on a fresh load.
    const adjusted = applySourceAdjust(gray, {
      brightness: store.get().sourceBrightness,
      contrast: store.get().sourceContrast,
      gamma: 1,
    });
    // Reset all per-image segmentation state (useRawLabelmap, assetLibrary,
    // assetData, remap dials, originalLabelmapImage, etc.) so stale data from
    // a previous image cannot affect the new one.
    store.patch({
      ...createResetSegmentationPatch(),
      source: adjusted,
      sourceOriginal: gray,
      lastGpuDiagnostics: null,
    });
    // Item 15 / Fix 4: clear the labelmap status line so stale
    // "carried over …" / "remapped to k=…" text does not persist.
    areaMaskHandles.setLabelmapStatus(null);
    zoomStore.fit(
      { w: bitmap.width, h: bitmap.height },
      { w: canvases.viewport.clientWidth, h: canvases.viewport.clientHeight },
    );
  }

  // Reset to empty state. Called from the Clear button. Reverses the
  // visual + state effects of `loadImage` so the dropzone shows again
  // and the user can pick a new file without confusing the dither
  // pipeline.
  function clearImage(): void {
    clearLastResult();
    setOutputActionsEnabled(false);
    canvases.setHasImage(false);
    canvases.source.width = 0;
    canvases.source.height = 0;
    canvases.result.width = 0;
    canvases.result.height = 0;
    canvases.overlay.width = 0;
    canvases.overlay.height = 0;
    canvases.overlay.style.visibility = "hidden";
    canvases.frame.style.width = "";
    canvases.frame.style.height = "";
    canvases.stack.style.width = "";
    canvases.stack.style.height = "";
    clearButton.disabled = true;
    clearButton.setAttribute("aria-disabled", "true");
    syncLoadMaskEnabled(false);
    // R10 Yuko spec: revert top-bar controls to disabled when image cleared.
    topBarHandles.syncImageGated(false);
    syncSidebarDrawerEnabled(false);
    fileInput.value = "";
    maskInput.value = "";
    // Reset all per-image segmentation state so a subsequent load starts
    // from a fully clean slate.
    store.patch({
      ...createResetSegmentationPatch(),
      source: null,
      sourceOriginal: null,
      lastGpuDiagnostics: null,
      referenceOverlay: { image: null, opacity: 1, enabled: true },
    });
    syncZoomMode("custom");
    viewMode.set("result");
    syncViewToggleLabel("result");
    statusBar.update({
      loaded: false,
      outW: 0,
      outH: 0,
      processedW: 0,
      processedH: 0,
      elapsedMs: 0,
      processing: false,
      methodLabel: null,
      messageKey: null,
    });
    // Item 15: clear the labelmap carry-over status line on image reset.
    areaMaskHandles.setLabelmapStatus(null);
  }

  function embeddedAssetsFromState(state: DemoState): EmbeddedAssetInput[] {
    return state.assetLibrary.flatMap((asset) => {
      const image = state.assetData.get(asset.id);
      return image === undefined ? [] : [{ asset, image }];
    });
  }

  function saveProjectFile(): void {
    const state = store.get();
    if (state.source === null) return;
    const archive = encodeProjectBundleArchive({
      state,
      assets: embeddedAssetsFromState(state),
      templateStackState: {
        activeTemplateId: state.templateStack.activeTemplateId,
        name: state.templateStack.name,
        groups: state.templateStack.groups,
        stacks: state.templateStack.stacks,
        bindings: state.templateStack.bindings,
      },
    });
    downloadBlob(
      new Blob([new Uint8Array(archive)], { type: "application/zip" }),
      "amitone-project.amtn",
    );
  }

  async function loadProjectFile(file: File): Promise<void> {
    try {
      const project = decodeProjectBundle(await file.arrayBuffer());
      applyProjectBundle(project);
    } catch {
      showToast("toast.project.invalid");
    } finally {
      topBarHandles.projectInput.value = "";
    }
  }

  function syncLoadedProjectVisuals(source: ImageData | null): void {
    if (source === null) {
      clearLastResult();
      setOutputActionsEnabled(false);
      canvases.setHasImage(false);
      clearButton.disabled = true;
      clearButton.setAttribute("aria-disabled", "true");
      syncLoadMaskEnabled(false);
      topBarHandles.syncImageGated(false);
      syncSidebarDrawerEnabled(false);
      return;
    }

    canvases.source.width = source.width;
    canvases.source.height = source.height;
    canvases.result.width = source.width;
    canvases.result.height = source.height;
    canvases.overlay.width = source.width;
    canvases.overlay.height = source.height;
    canvases.frame.style.width = `${source.width}px`;
    canvases.frame.style.height = `${source.height}px`;
    canvases.stack.style.width = `${source.width}px`;
    canvases.stack.style.height = `${source.height}px`;
    const ctx = canvases.source.getContext("2d");
    if (ctx) ctx.putImageData(source, 0, 0);
    canvases.setHasImage(true);
    clearLastResult();
    setOutputActionsEnabled(false);
    clearButton.disabled = false;
    clearButton.removeAttribute("aria-disabled");
    syncLoadMaskEnabled(true);
    topBarHandles.syncImageGated(true);
    syncSidebarDrawerEnabled(true);
    zoomStore.fit(
      { w: source.width, h: source.height },
      { w: canvases.viewport.clientWidth, h: canvases.viewport.clientHeight },
    );
  }

  function applyProjectBundle(project: DecodedProjectBundle): void {
    const source = project.source;
    const assetLibrary =
      project.assetLibrary.length > 0 ? project.assetLibrary : project.assets;
    store.patch({
      ...methodSnapshotToPresetPatch(project.presets.global),
      ...binaryPatchForMethodSnapshot(project.presets.global, project.assetData),
      source,
      sourceOriginal: project.sourceOriginal ?? source,
      labelMap: project.labelMap,
      regions: project.regions.regions,
      regionEntries: project.regions.regionEntries,
      regionStacks: project.regions.regionStacks,
      regionLabelPlane: project.regions.regionLabelPlane,
      activeRegionUuids: project.regions.activeRegionUuids,
      activeStackUuid: project.regions.activeStackUuid,
      remapMaxK: project.regions.remap.maxK,
      remapDeltaE: project.regions.remap.deltaE,
      remapPreset: project.regions.remap.preset,
      useRawLabelmap: project.regions.remap.useRawLabelmap,
      originalLabelmapImage: project.labelMap,
      assetLibrary,
      assetData: project.assetData,
      assetDataAccessOrder: Array.from(project.assetData.keys()),
      referenceOverlay: project.referenceOverlay,
      templateStack: {
        activeTemplateId: project.templateStackState.activeTemplateId,
        name: project.templateStackState.name,
        groups: project.templateStackState.groups,
        stacks: project.templateStackState.stacks,
        bindings: project.templateStackState.bindings,
      },
      tonePreset: project.presets.tonePreset,
      sourceBrightness: project.sourceAdjust.global.brightness,
      sourceContrast: project.sourceAdjust.global.contrast,
      sourceAdjustScope: project.sourceAdjust.scope,
      regionSourceAdjust: project.sourceAdjust.regionSourceAdjust,
      regionUndoStack: [],
      lastGpuDiagnostics: null,
    });
    syncLoadedProjectVisuals(source);
    areaMaskHandles.setLabelmapStatus(null);
  }

  function restoreCanonicalState(snapshot: DemoState): void {
    isRestoringCanonicalHistory = true;
    try {
      store.patch(snapshot);
    } finally {
      isRestoringCanonicalHistory = false;
    }
    previousCanonicalState = cloneCanonicalSnapshot(snapshot);
    syncLoadedProjectVisuals(snapshot.source);
    areaMaskHandles.setLabelmapStatus(null);
    syncHistoryButtons();
  }

  function undoCanonicalState(): void {
    const result = undoCanonicalHistory(canonicalHistory, store.get());
    if (result.history === canonicalHistory) return;
    canonicalHistory = result.history;
    restoreCanonicalState(result.snapshot);
  }

  function redoCanonicalState(): void {
    const result = redoCanonicalHistory(canonicalHistory, store.get());
    if (result.history === canonicalHistory) return;
    canonicalHistory = result.history;
    restoreCanonicalState(result.snapshot);
  }

  function applyImportedPresetPatch(patch: Partial<DemoState>): void {
    const state = store.get();
    const nextUndoStack = pushUndoSnapshot(state);
    if (state.activeStackUuid !== null || state.activeRegionUuids.length > 0) {
      store.patch({
        ...applyMethodPartial(state, patch as MethodPartial),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }
    store.patch({
      ...patch,
      tonePreset: "custom",
      regionUndoStack: nextUndoStack,
    });
  }

  function applyImportedPresetSnapshot(
    snapshot: ReturnType<typeof buildMethodSnapshot>,
    assetPatch: Pick<
      DemoState,
      "assetLibrary" | "assetData" | "assetDataAccessOrder"
    >,
  ): void {
    const state = store.get();
    const nextUndoStack = pushUndoSnapshot(state);

    if (state.activeStackUuid !== null) {
      store.patch({
        ...assetPatch,
        regionStacks: state.regionStacks.map((stack) =>
          stack.uuid === state.activeStackUuid
            ? { ...stack, override: snapshot }
            : stack,
        ),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }

    if (state.activeRegionUuids.length > 0) {
      const active = new Set(state.activeRegionUuids);
      store.patch({
        ...assetPatch,
        regionEntries: state.regionEntries.map((entry) =>
          active.has(entry.uuid) ? { ...entry, override: snapshot } : entry,
        ),
        tonePreset: "custom",
        regionUndoStack: nextUndoStack,
      });
      return;
    }

    store.patch({
      ...assetPatch,
      ...methodSnapshotToPresetPatch(snapshot),
      ...binaryPatchForMethodSnapshot(snapshot, assetPatch.assetData),
      tonePreset: "custom",
      regionUndoStack: nextUndoStack,
    });
  }

  async function importPresetFile(file: File): Promise<void> {
    try {
      const bytes = await file.arrayBuffer();
      try {
        const bundle = decodePresetBundle(bytes);
        const assetPatch = mergeEmbeddedAssetsIntoState(
          store.get(),
          bundle.assets,
          bundle.assetData,
        );
        applyImportedPresetSnapshot(bundle.preset, assetPatch);
        return;
      } catch {
        const text = new TextDecoder().decode(bytes);
        const validated = validateImportedPreset(JSON.parse(text));
        if (validated === null) throw new Error("Invalid preset");
        applyImportedPresetPatch(validated.patch);
      }
    } catch {
      showToast("status.tonePreset.invalidFile");
    } finally {
      topBarHandles.presetInput.value = "";
    }
  }

  function currentGlobalPresetForBundle(state: DemoState): {
    preset: ReturnType<typeof buildMethodSnapshot>;
    assets: EmbeddedAssetInput[];
  } {
    const effective = effectiveDialValues(state);
    let effectiveState: DemoState = {
      ...state,
      ...effective,
      matrixSize: effective.matrixSize as DemoState["matrixSize"],
    };
    let patternRefId: PatternAssetId | null = null;
    let stampMaskRefId: StampMaskAssetId | null = null;

    if (effectiveState.family === "B" && effectiveState.patternImage) {
      const result = registerAsset(
        effectiveState.assetLibrary,
        effectiveState.assetData,
        effectiveState.assetDataAccessOrder,
        effectiveState.patternImage,
        "pattern",
      );
      effectiveState = {
        ...effectiveState,
        assetLibrary: result.assetLibrary,
        assetData: result.assetData,
        assetDataAccessOrder: result.assetDataAccessOrder,
      };
      patternRefId = result.asset.id as PatternAssetId;
    }

    const needsStampMask =
      (effectiveState.family !== "B" && effectiveState.stampType === "custom") ||
      (effectiveState.family === "B" &&
        effectiveState.patternHalftoneStampType === "custom");
    if (needsStampMask && effectiveState.customStampMask) {
      const result = registerAsset(
        effectiveState.assetLibrary,
        effectiveState.assetData,
        effectiveState.assetDataAccessOrder,
        effectiveState.customStampMask,
        "stampMask",
      );
      effectiveState = {
        ...effectiveState,
        assetLibrary: result.assetLibrary,
        assetData: result.assetData,
        assetDataAccessOrder: result.assetDataAccessOrder,
      };
      stampMaskRefId = result.asset.id as StampMaskAssetId;
    }

    const preset = buildMethodSnapshot(effectiveState, stampMaskRefId, patternRefId);
    return {
      preset,
      assets: embeddedAssetsForMethodSnapshot(effectiveState, preset),
    };
  }

  function currentPresetForBundle(): {
    name: string;
    preset: ReturnType<typeof buildMethodSnapshot>;
    assets: EmbeddedAssetInput[];
  } {
    const state = store.get();
    if (state.activeStackUuid !== null) {
      const stack = state.regionStacks.find(
        (candidate) => candidate.uuid === state.activeStackUuid,
      );
      if (stack?.override) {
        return {
          name: stack.userName,
          preset: stack.override,
          assets: embeddedAssetsForMethodSnapshot(state, stack.override),
        };
      }
    }
    const activeUuid = state.activeRegionUuids[0];
    if (activeUuid !== undefined) {
      const entry = state.regionEntries.find(
        (candidate) => candidate.uuid === activeUuid,
      );
      if (entry?.override) {
        return {
          name: `Region ${entry.label}`,
          preset: entry.override,
          assets: embeddedAssetsForMethodSnapshot(state, entry.override),
        };
      }
    }
    const global = currentGlobalPresetForBundle(state);
    return {
      name: i18n.t("preset.custom.name"),
      ...global,
    };
  }

  function exportCurrentPresetFile(): void {
    downloadPresetAsBundle(currentPresetForBundle());
  }

  function templatePresetForRegion(
    state: DemoState,
    entryUuid: string,
    globalPreset: {
      preset: ReturnType<typeof buildMethodSnapshot>;
      assets: EmbeddedAssetInput[];
    },
  ): {
    preset: ReturnType<typeof buildMethodSnapshot>;
    assets: EmbeddedAssetInput[];
  } {
    const entry = state.regionEntries.find((candidate) => candidate.uuid === entryUuid);
    if (entry?.override) {
      return {
        preset: entry.override,
        assets: embeddedAssetsForMethodSnapshot(state, entry.override),
      };
    }
    if (entry?.stackId) {
      const stack = state.regionStacks.find((candidate) => candidate.uuid === entry.stackId);
      if (stack?.override) {
        return {
          preset: stack.override,
          assets: embeddedAssetsForMethodSnapshot(state, stack.override),
        };
      }
    }
    return globalPreset;
  }

  function uniqueEmbeddedAssetInputs(
    assets: ReadonlyArray<EmbeddedAssetInput>,
  ): EmbeddedAssetInput[] {
    const byId = new Map<string, EmbeddedAssetInput>();
    for (const asset of assets) {
      byId.set(asset.asset.id, asset);
    }
    return Array.from(byId.values());
  }

  function exportRegionTemplateFile(): void {
    const state = store.get();
    if (state.regionEntries.length === 0) return;
    const globalPreset = currentGlobalPresetForBundle(state);
    const groupBundles = state.regionEntries.map((entry) => {
      const bundle = templatePresetForRegion(state, entry.uuid, globalPreset);
      return {
        group: {
          templateRegionId: entry.uuid,
          rgb: entry.rgb,
          preset: bundle.preset,
          stackId: entry.stackId,
        },
        assets: bundle.assets,
      };
    });
    const stackBundles = state.regionStacks.map((stack) => ({
      stack: {
        templateStackId: stack.uuid,
        userName: stack.userName,
        preset: stack.override,
      },
      assets:
        stack.override === null
          ? []
          : embeddedAssetsForMethodSnapshot(state, stack.override),
    }));
    const groups = groupBundles.map((bundle) => bundle.group);
    const stacks = stackBundles.map((bundle) => bundle.stack);
    const archive = encodeRegionTemplateBundleArchive({
      name: state.templateStack.name ?? "Region template",
      groups,
      stacks,
      assets: uniqueEmbeddedAssetInputs([
        ...groupBundles.flatMap((bundle) => bundle.assets),
        ...stackBundles.flatMap((bundle) => bundle.assets),
      ]),
    });
    downloadBlob(
      new Blob([new Uint8Array(archive)], { type: "application/zip" }),
      "amitone-template.amtt",
    );
  }

  async function importRegionTemplateFile(file: File): Promise<void> {
    try {
      const bundle = decodeRegionTemplateBundle(await file.arrayBuffer());
      const state = store.get();
      const assetPatch = mergeEmbeddedAssetsIntoState(
        state,
        bundle.assets,
        bundle.assetData,
      );
      const bound = bindTemplateGroupsForImport(state, bundle.groups, bundle.bindings);
      store.patch({
        ...assetPatch,
        regionEntries: bound.regionEntries,
        templateStack: {
          activeTemplateId: file.name,
          name: bundle.name,
          groups: bundle.groups,
          stacks: bundle.stacks,
          bindings: bound.bindings,
        },
        uiActiveTab: "template",
        regionUndoStack: pushUndoSnapshot(state),
      });
    } catch {
      showToast("toast.template.invalid");
    }
  }

  function rebindTemplateRegion(templateRegionId: string, regionUuid: string | null): void {
    const state = store.get();
    const group = state.templateStack.groups.find(
      (candidate) => candidate.templateRegionId === templateRegionId,
    );
    const region = regionUuid === null
      ? null
      : state.regionEntries.find((candidate) => candidate.uuid === regionUuid) ?? null;
    const nextBindings = state.templateStack.bindings.map((binding) =>
      binding.templateRegionId === templateRegionId
        ? {
            ...binding,
            boundRegionUuid: region?.uuid ?? null,
            boundLabel: region?.label ?? null,
            ...(region === null ? {} : { candidateRegionUuids: [] }),
          }
        : binding,
    );
    const nextEntries =
      group?.preset && region
        ? state.regionEntries.map((entry) =>
            entry.uuid === region.uuid ? { ...entry, override: group.preset } : entry,
          )
        : state.regionEntries;
    store.patch({
      regionEntries: nextEntries,
      templateStack: { ...state.templateStack, bindings: nextBindings },
      regionUndoStack: pushUndoSnapshot(state),
    });
  }

  // The clear button click listener is wired inside createTopBar via the
  // onClear callback; no need to add it again here.

  function readDroppedAsImageData(file: File): Promise<ImageData> {
    return createImageBitmap(file).then((bitmap) => {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("2d context unavailable");
      ctx.drawImage(bitmap, 0, 0);
      return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    });
  }

  // The fileInput change listener is wired inside createTopBar via onFileChange.
  // The maskInput change listener is wired inside createTopBar via onMaskChange
  // (after the defensive guard that calls getSource()).
  // The fit/1:1/viewToggle listeners are wired inside createTopBar via the
  // corresponding callbacks.

  // ── Load mask handler - called by createTopBar after the defensive guard ──
  //
  // Reads the selected PNG as a labelmap. Preserves transparent pixels
  // (same behaviour as the sidebar upload row that this replaces).
  function rebindTemplateStackAfterLabelmapPatch(
    state: DemoState,
    patch: Partial<DemoState>,
  ): Partial<DemoState> {
    if (state.templateStack.groups.length === 0 || patch.regionEntries === undefined) {
      return patch;
    }
    const nextState = { ...state, ...patch };
    const bound = bindTemplateGroupsForImport(
      nextState,
      state.templateStack.groups,
      state.templateStack.bindings,
    );
    const previousTemplateBoundUuids = new Set(
      state.templateStack.bindings.flatMap((binding) =>
        binding.boundRegionUuid === null ? [] : [binding.boundRegionUuid],
      ),
    );
    const nextTemplateBoundUuids = new Set(
      bound.bindings.flatMap((binding) =>
        binding.boundRegionUuid === null ? [] : [binding.boundRegionUuid],
      ),
    );
    return {
      ...patch,
      regionEntries: bound.regionEntries.map((entry) =>
        previousTemplateBoundUuids.has(entry.uuid) &&
        !nextTemplateBoundUuids.has(entry.uuid)
          ? { ...entry, override: null }
          : entry,
      ),
      templateStack: {
        ...state.templateStack,
        bindings: bound.bindings,
      },
    };
  }

  function handleMaskFile(file: File): void {
    void createImageBitmap(file)
      .then((bitmap) => {
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("2d context unavailable");
        ctx.drawImage(bitmap, 0, 0);
        return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
      })
      .then((image) => {
        const state = store.get();
        const { patch, carryOverStats, preprocessStats } = buildLabelmapPatch(
          image,
          state.source,
          state.regionEntries,
          (label) => i18n.t("label.regionN", { n: label }),
          state.activeRegionUuids,
          !state.useRawLabelmap,
        );
        store.patch(rebindTemplateStackAfterLabelmapPatch(state, patch));
        areaMaskHandles.setLabelmapStatus(carryOverStats, preprocessStats);
      })
      .catch(() => {
        store.patch({ labelmapError: "error.labelmap.invalidImage" });
        areaMaskHandles.setLabelmapStatus(null);
      })
      .finally(() => {
        maskInput.value = "";
      });
  }

  // Subscribe zoomStore so the top-bar zoom input and pressed state stay in sync.
  // When the user scrolls/pinches to change zoom, the aria-pressed state on Fit /
  // 1:1 / 200% must clear. We infer the mode from the current zoom value (Item 2).
  zoomStore.subscribe(() => {
    const z = zoomStore.get().zoom;
    syncZoomDisplay(Math.round(z * 100));

    // Determine which preset mode (if any) the current zoom matches.
    const EPS = 0.001;
    if (Math.abs(z - lastFitZoom) < EPS) {
      syncZoomMode("fit");
    } else if (Math.abs(z - 1) < EPS) {
      syncZoomMode("1:1");
    } else if (Math.abs(z - 2) < EPS) {
      syncZoomMode("200%");
    } else {
      syncZoomMode("custom");
    }
  });
  // Seed the initial display value.
  syncZoomDisplay(Math.round(zoomStore.get().zoom * 100));
  syncZoomMode("fit");

  // Subscribe viewMode so the toggle button label stays in sync.
  viewMode.subscribe((mode) => {
    syncViewToggleLabel(mode);
    if (lastResult !== null) {
      const ctx = canvases.result.getContext("2d");
      if (ctx) {
        ctx.putImageData(
          compositeReferenceOverlay(
            lastResult,
            referenceOverlayForOutput(store.get(), lastResult),
          ),
          0,
          0,
        );
        drawRegionOverlay(store.get());
      }
    }
  });
  i18n.onLocaleChange(() => {
    syncViewToggleLabel(viewMode.get());
  });

  canvases.viewport.addEventListener("dragover", (event) => {
    event.preventDefault();
    canvases.viewport.dataset["dragover"] = "true";
  });
  canvases.viewport.addEventListener("dragleave", () => {
    delete canvases.viewport.dataset["dragover"];
  });
  canvases.viewport.addEventListener("drop", (event) => {
    event.preventDefault();
    delete canvases.viewport.dataset["dragover"];
    const file = event.dataTransfer
      ? firstImageFile(event.dataTransfer.files)
      : null;
    if (!file) return;
    const currentSource = store.get().source;
    if (currentSource) {
      void readDroppedAsImageData(file)
        .then((image) => {
          if (
            image.width === currentSource.width &&
            image.height === currentSource.height
          ) {
            // Pass existing regionEntries so re-upload can carry over
            // UUIDs / overrides / names for colours that still exist
            // (plan A3 "External labelmap re-upload").
            const state = store.get();
            const { patch, carryOverStats, preprocessStats } =
              buildLabelmapPatch(
                image,
                currentSource,
                state.regionEntries,
                (label) => i18n.t("label.regionN", { n: label }),
                state.activeRegionUuids,
              );
            store.patch(rebindTemplateStackAfterLabelmapPatch(state, patch));
            // Item 15 + Item 21: surface carry-over and preprocess stats.
            areaMaskHandles.setLabelmapStatus(carryOverStats, preprocessStats);
          } else {
            void loadImage(file);
          }
        })
        .catch(() => {
          void loadImage(file);
        });
      return;
    }
    void loadImage(file);
  });

  window.addEventListener("dragover", (event) => {
    if (store.get().source) return;
    const hasImage = event.dataTransfer
      ? Array.from(event.dataTransfer.items).some(
          (item) =>
            item.kind === "file" &&
            (item.type === "" || item.type.startsWith("image/")),
        )
      : true;
    if (!hasImage) return;
    event.preventDefault();
    canvases.viewport.dataset["dragover"] = "true";
  });
  window.addEventListener("drop", (event) => {
    if (store.get().source) return;
    const file = event.dataTransfer
      ? firstImageFile(event.dataTransfer.files)
      : null;
    if (!file) return;
    event.preventDefault();
    delete canvases.viewport.dataset["dragover"];
    void loadImage(file);
  });
  document.addEventListener("paste", (event) => {
    // Block paste-as-load while an image is already loaded so the
    // user's in-progress dither isn't overwritten by an accidental
    // ⌘V. They have to click Clear first.
    if (store.get().source) return;
    const file = event.clipboardData
      ? firstImageClipboardItem(event.clipboardData.items)
      : null;
    if (file) {
      event.preventDefault();
      void loadImage(file);
    }
  });

  // ========== render loop ==========
  let debounceId: ReturnType<typeof setTimeout> | null = null;
  let scheduledRenderToken: number | null = null;
  let renderInFlight = false;

  function currentExportScale(): number {
    return Math.max(
      1,
      Math.min(4, Number(controlsHandles.exportScaleSelect.value) || 1),
    );
  }

  function setOutputActionsEnabled(enabled: boolean): void {
    controlsHandles.exportButton.disabled = !enabled;
    controlsHandles.copyButton.disabled = !enabled;
    topBarHandles.syncOutputActions({
      canExport: enabled,
      scale: controlsHandles.exportScaleSelect.value as "1" | "2" | "4",
    });
  }

  /**
   * Fallback path, not normal operation. This firing in production is a bug
   * signal that the adjusted source was missing while the original source
   * was still present.
   */
  function recoverSourceState(state: DemoState): DemoState {
    const result = recoverSourceStateFallback(state, buildAdjustedSource);
    if (result.fired) {
      store.patch({ source: result.recovered.source });
    }
    return result.recovered;
  }

  async function resultToPngBlob(scale: number): Promise<Blob | null> {
    const state = recoverSourceState(store.get());
    if (!state.source) return null;

    const renderTokenAtExport = renderToken;
    const includeReferenceOverlay = includeReferenceOverlayInExport;
    if (
      scale <= 1 &&
      lastResult !== null &&
      lastResultToken !== renderTokenAtExport
    ) {
      await waitForRenderToken(renderTokenAtExport);
      if (
        renderTokenAtExport !== renderToken ||
        lastResult === null ||
        lastResultToken !== renderTokenAtExport
      ) {
        return null;
      }
    }

    if (scale <= 1 && lastResult) {
      // Fast path: nearest-neighbour the existing 1x lastResult.
      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = lastResult.width;
      sourceCanvas.height = lastResult.height;
      const sourceCtx = sourceCanvas.getContext("2d");
      if (!sourceCtx) return null;
      sourceCtx.imageSmoothingEnabled = false;
      sourceCtx.putImageData(
        compositeReferenceOverlay(
          lastResult,
          referenceOverlayForOutput(
            state,
            lastResult,
            1,
            includeReferenceOverlay,
          ),
        ),
        0,
        0,
      );
      return new Promise((resolve) => {
        sourceCanvas.toBlob(resolve, "image/png");
      });
    }

    if (!lastResult && scale <= 1) return null;

    // Re-render path. Upscale source + labelmap via nearest-neighbour
    // at the target resolution, then run dither() with cellSize * scale
    // so the dot pitch stays constant in source-space.
    const upSource = nearestUpscale(state.source, scale);
    const upLabelMap = state.labelMap
      ? nearestUpscale(state.labelMap, scale)
      : null;

    const token = exportRenderToken;
    const result = await dither(
      upLabelMap
        ? {
            source: upSource,
            labelMap: upLabelMap,
            regions: buildRegionsAtScale(state, scale),
          }
        : {
            source: upSource,
            regions: [{ label: 0, method: buildMethodAtScale(state, scale) }],
          },
    );
    if (token !== exportRenderToken) return null;

    const outCanvas = document.createElement("canvas");
    outCanvas.width = result.output.width;
    outCanvas.height = result.output.height;
    const ctx = outCanvas.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    const output = compositeReferenceOverlay(
      result.output,
      referenceOverlayForOutput(
        state,
        result.output,
        scale,
        includeReferenceOverlay,
      ),
    );
    ctx.putImageData(output, 0, 0);
    return new Promise((resolve) => outCanvas.toBlob(resolve, "image/png"));
  }

  function downloadBlob(blob: Blob, filename = "amitone.png"): void {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function flashButtonText(
    button: HTMLButtonElement,
    key: string,
    delayMs = 1000,
  ): void {
    const previous = button.textContent;
    button.textContent = i18n.t(key);
    window.setTimeout(() => {
      button.textContent = previous;
    }, delayMs);
  }

  async function render(): Promise<void> {
    const token = renderToken;
    const initialState = store.get();
    if (initialState.source === null && initialState.sourceOriginal !== null) {
      recoverSourceState(initialState);
      notifyRenderSettled(token);
      return;
    }
    const state = initialState;
    if (!state.source) {
      statusBar.update({
        loaded: false,
        outW: 0,
        outH: 0,
        processedW: 0,
        processedH: 0,
        elapsedMs: 0,
        processing: false,
        methodLabel: null,
        messageKey: state.labelmapError,
      });
      notifyRenderSettled(token);
      return;
    }
    const sw = state.source.width;
    const sh = state.source.height;
    statusBar.update({
      loaded: true,
      outW: sw,
      outH: sh,
      processedW: 0,
      processedH: 0,
      elapsedMs: 0,
      processing: true,
      ...statusBaseFor(state),
    });
    try {
      const input: DitherInput = state.labelMap
        ? {
            source: state.source,
            labelMap: state.labelMap,
            regions: buildRegions(state),
          }
        : {
            source: state.source,
            regions: [{ label: 0, method: buildMethod(state) }],
          };
      let frameDiagnostics: GpuRenderDiagnostics | null = null;
      const backend = state.gpuEnabled ? await ensureGpuBackend() : null;
      if (token !== renderToken) return;
      const ditherOptions: DitherOptions | undefined = backend
        ? {
            backend: observeFrameBackend(backend, (diagnostics) => {
              frameDiagnostics = diagnostics;
            }),
            onGpuFallback: (diagnostics) => {
              frameDiagnostics = diagnostics;
            },
          }
        : undefined;

      const result = await dither(input, ditherOptions);
      if (token !== renderToken) return;
      if (frameDiagnostics !== null) {
        store.patch({ lastGpuDiagnostics: frameDiagnostics });
      } else if (state.gpuEnabled) {
        store.patch({
          lastGpuDiagnostics: cpuDiagnosticsForInput(input, gpuLastError),
        });
      } else if (!state.gpuEnabled && store.get().lastGpuDiagnostics !== null) {
        store.patch({ lastGpuDiagnostics: null });
      }
      const ctx = canvases.result.getContext("2d");
      if (!ctx) throw new Error("2d context unavailable");
      const output = compositeReferenceOverlay(
        result.output,
        referenceOverlayForOutput(state, result.output),
      );
      ctx.putImageData(output, 0, 0);
      // Store the exported/result pixels before drawing the UI selection
      // overlay. Reference overlay pixels are composited later for preview
      // and export, so opacity toggles never re-run the dither path.
      lastResult = result.output;
      lastResultToken = token;
      drawRegionOverlay(state);
      setOutputActionsEnabled(true);
      statusBar.update({
        loaded: true,
        outW: result.outputSize.w,
        outH: result.outputSize.h,
        processedW: result.processedSize.w,
        processedH: result.processedSize.h,
        elapsedMs: result.elapsedMs,
        processing: false,
        ...statusBaseFor(state),
      });
    } catch (err) {
      if (token !== renderToken) return;
      console.error("render failed", err);
      statusBar.update({
        loaded: true,
        outW: sw,
        outH: sh,
        processedW: 0,
        processedH: 0,
        elapsedMs: 0,
        processing: false,
        ...statusBaseFor(state),
      });
    } finally {
      notifyRenderSettled(token);
    }
  }

  function scheduleRender(): void {
    const previousScheduledRenderToken = scheduledRenderToken;
    renderToken += 1;
    exportRenderToken = renderToken;
    if (debounceId !== null) {
      clearTimeout(debounceId);
      debounceId = null;
      if (previousScheduledRenderToken !== null) {
        notifyRenderSettled(previousScheduledRenderToken);
      }
    } else if (previousScheduledRenderToken !== null) {
      notifyRenderSettled(previousScheduledRenderToken);
    }
    scheduledRenderToken = renderToken;
    if (renderInFlight) return;
    armScheduledRender();
  }

  function armScheduledRender(): void {
    if (debounceId !== null || scheduledRenderToken === null) return;
    debounceId = setTimeout(
      () => {
        debounceId = null;
        runScheduledRender();
      },
      renderDebounceMsForSource(store.get().source, isIpadLike()),
    );
  }

  function runScheduledRender(): void {
    if (renderInFlight || scheduledRenderToken === null) return;
    scheduledRenderToken = null;
    renderInFlight = true;
    void render().finally(() => {
      renderInFlight = false;
      armScheduledRender();
    });
  }

  const hiddenRelease = createHiddenReleaseScheduler({
    onRelease: clearLastResult,
    onVisible: () => {
      if (lastResult === null) scheduleRender();
    },
  });
  document.addEventListener(
    "visibilitychange",
    hiddenRelease.handleVisibilityChange,
  );

  // Re-derive `state.source` from `state.sourceOriginal` when source-adjust
  // values change, or when existing adjustments need to be recomposited over
  // a changed mask, stack, or label layout.
  store.subscribe((state, patch) => {
    if (shouldRebuildAdjustedSourceForPatch(state, patch)) {
      // G2: apply global source-adjust first, then composite per-region overrides.
      const adjusted = buildAdjustedSource(state);
      store.patch({ source: adjusted });
      return; // The recursive patch will trigger render via the next subscribe call.
    }

    // Sync re-flood cursor whenever refloodMode changes.
    if ("refloodMode" in patch) {
      syncRefloodCursor(state.refloodMode);
    }

    const onlyGpuRuntimeStateChanged = Object.keys(patch).every(
      (k) => k === "gpuEnabledForSession" || k === "lastGpuDiagnostics",
    );
    if (onlyGpuRuntimeStateChanged) return;

    // Fast path: when only overlay UI state changes, repaint the clean result
    // and redraw the overlay without re-running the dither pipeline.
    const onlyOverlayChanged = Object.keys(patch).every(
      (k) =>
        k === "activeRegionUuids" ||
        k === "refloodMode" ||
        k === "referenceOverlay" ||
        k === "uiActiveTab",
    );
    if (onlyOverlayChanged && lastResult !== null) {
      const ctx = canvases.result.getContext("2d");
      if (ctx) {
        ctx.putImageData(
          compositeReferenceOverlay(
            lastResult,
            referenceOverlayForOutput(state, lastResult),
          ),
          0,
          0,
        );
        drawRegionOverlay(state);
      }
      return;
    }

    scheduleRender();
  });

  controlsHandles.exportButton.addEventListener("click", () => {
    const scale = currentExportScale();
    if (scale > 1) {
      flashButtonText(controlsHandles.exportButton, "action.exporting", 10000);
    }
    void resultToPngBlob(scale).then((blob) => {
      if (scale > 1) {
        // Restore the original label now that the re-render is done.
        const key =
          controlsHandles.exportButton.dataset["i18n"] ?? "action.export";
        controlsHandles.exportButton.textContent = i18n.t(key);
      }
      if (blob) downloadBlob(blob);
    });
  });

  controlsHandles.copyButton.addEventListener("click", () => {
    if (
      !store.get().source ||
      !navigator.clipboard ||
      typeof ClipboardItem === "undefined"
    ) {
      flashButtonText(controlsHandles.copyButton, "action.copy_failed");
      return;
    }

    // Safari requires `navigator.clipboard.write()` to be called
    // synchronously inside the user-gesture handler; awaiting the blob
    // first would expire the gesture and the write rejects with
    // NotAllowedError. ClipboardItem accepts `Promise<Blob>` for each
    // mime type, so we hand the still-pending PNG encode straight in
    // without awaiting first.
    const blobPromise = resultToPngBlob(currentExportScale()).then((blob) => {
      if (!blob) throw new Error("failed to encode PNG");
      return blob;
    });

    navigator.clipboard
      .write([new ClipboardItem({ "image/png": blobPromise })])
      .then(() => {
        flashButtonText(controlsHandles.copyButton, "action.copied");
      })
      .catch((err) => {
        console.error("copy failed", err);
        flashButtonText(controlsHandles.copyButton, "action.copy_failed");
      });
  });
}

// B0.2 / B0.3 GPU proof probe. Active only in Vite dev mode when the
// standalone gate visits `/?__b0Probe=1`; production builds drop this block.
if (
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).has("__b0Probe")
) {
  void (async () => {
    const [{ WebGL2DitherBackend, createDitherGpuBackend, dither: coreDither }] =
      await Promise.all([import("@amitone/dither-core")]);
    const canvas = document.createElement("canvas");
    canvas.width = 4;
    canvas.height = 4;

    function checksumBytes(bytes: Uint8ClampedArray): number {
      let sum = 0;
      for (let i = 0; i < bytes.length; i += 1) {
        sum = (sum + (bytes[i] ?? 0) * (i + 1)) >>> 0;
      }
      return sum;
    }

    function usedHeapSize(): number | null {
      const perf = performance as Performance & {
        memory?: { usedJSHeapSize?: number };
      };
      const value = perf.memory?.usedJSHeapSize;
      return typeof value === "number" ? value : null;
    }

    function buildBayerPipelineSource(): ImageData {
      const data = new Uint8ClampedArray(8 * 8 * 4);
      for (let y = 0; y < 8; y += 1) {
        for (let x = 0; x < 8; x += 1) {
          const idx = (y * 8 + x) * 4;
          const value = x < 4 ? 96 : 192;
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, 8, 8);
    }

    function buildRegionBatchSource(): ImageData {
      const data = new Uint8ClampedArray(8 * 8 * 4);
      for (let y = 0; y < 8; y += 1) {
        for (let x = 0; x < 8; x += 1) {
          const idx = (y * 8 + x) * 4;
          const value = x < 3 ? 16 : x >= 5 ? 240 : 128;
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, 8, 8);
    }

    function buildRegionBatchLabelMap(): ImageData {
      const data = new Uint8ClampedArray(8 * 8 * 4);
      for (let y = 0; y < 8; y += 1) {
        for (let x = 0; x < 8; x += 1) {
          const idx = (y * 8 + x) * 4;
          data[idx] = x < 3 ? 1 : x >= 5 ? 2 : 99;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, 8, 8);
    }

    function buildRegionBatchRegions(): WebGL2BayerRegionBatchRegion[] {
      return [
        {
          label: 1,
          method: {
            family: "A",
            algorithm: "bayer",
            matrixSize: 2,
            cellSize: 1,
            stamp: { type: "pixel", coverage: 1 },
            polarity: "black",
            density: 50,
          },
        },
        {
          label: 2,
          method: {
            family: "A",
            algorithm: "bayer",
            matrixSize: 2,
            cellSize: 1,
            stamp: { type: "pixel", coverage: 1 },
            polarity: "black",
            density: 100,
          },
        },
      ];
    }

    function buildBenchmarkSource(width: number, height: number): ImageData {
      const data = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          const value = (x + y) % 2 === 0 ? 96 : 192;
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function buildBenchmarkLabelMap(
      width: number,
      height: number,
      regionCount: number,
    ): ImageData {
      const data = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          const label = Math.min(
            regionCount,
            Math.floor((x * regionCount) / width) + 1,
          );
          data[idx] = label;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function buildBenchmarkRegions(
      width: number,
      regionCount: number,
    ): WebGL2BayerRegionBatchRegion[] {
      const large = width >= 3000;
      return Array.from({ length: regionCount }, (_unused, index) => ({
        label: index + 1,
        method: {
          family: "A",
          algorithm: "bayer",
          matrixSize: 2,
          cellSize: large ? 64 : 4,
          stamp: { type: "pixel", coverage: 1 },
          polarity: "black",
          density: 50,
        },
      }));
    }

    function buildB8BayerPixelMethod(cellSize: number): MethodParams {
      return {
        family: "A",
        algorithm: "bayer",
        matrixSize: 8,
        cellSize,
        stamp: { type: "pixel", coverage: 1 },
        polarity: "black",
        density: 50,
        blackThreshold: 0,
        whiteThreshold: 1,
        gridAngleDeg: 0,
        shapeAngleDeg: 0,
        algorithmJitter: 0,
      };
    }

    function buildB8ValidationSource(width: number, height: number): ImageData {
      const data = new Uint8ClampedArray(width * height * 4);
      const invW = 1 / Math.max(1, width - 1);
      const invH = 1 / Math.max(1, height - 1);
      for (let y = 0; y < height; y += 1) {
        const gy = y * invH;
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          const gx = x * invW;
          let value = Math.round(40 + (gx * 0.64 + gy * 0.36) * 176);
          if (
            x === Math.floor(width * 0.33) ||
            y === Math.floor(height * 0.5)
          ) {
            value = 0;
          } else if (x > width * 0.7 && y > height * 0.62) {
            value = 238;
          }
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function observeBackend(backend: DitherGpuBackend): {
      backend: DitherGpuBackend;
      capture: { diagnostics: GpuRenderDiagnostics | null };
    } {
      const capture: { diagnostics: GpuRenderDiagnostics | null } = {
        diagnostics: null,
      };
      return {
        capture,
        backend: {
          name: backend.name,
          initialize: () => backend.initialize(),
          isReady: () => backend.isReady(),
          getCaps: () => backend.getCaps(),
          renderRegionBatch: async (req) => {
            const result = await backend.renderRegionBatch(req);
            capture.diagnostics = result.diagnostics;
            return result;
          },
          dispose: () => backend.dispose(),
        },
      };
    }

    async function runB8DitherFrame(
      source: ImageData,
      backend: DitherGpuBackend,
    ): Promise<{
      diagnostics: GpuRenderDiagnostics | null;
      fallbackDiagnostics: GpuRenderDiagnostics[];
      outputChecksum: number;
      output: ImageData;
    }> {
      const observed = observeBackend(backend);
      const fallbackDiagnostics: GpuRenderDiagnostics[] = [];
      const result = await coreDither(
        {
          source,
          regions: [
            {
              label: 0,
              method: buildB8BayerPixelMethod(source.width >= 3000 ? 64 : 8),
            },
          ],
        },
        {
          backend: observed.backend,
          onGpuFallback: (diagnostics) => {
            fallbackDiagnostics.push(diagnostics);
          },
        },
      );
      return {
        diagnostics:
          observed.capture.diagnostics ??
          fallbackDiagnostics[fallbackDiagnostics.length - 1] ??
          null,
        fallbackDiagnostics,
        outputChecksum: checksumBytes(result.output.data),
        output: result.output,
      };
    }

    function sleep(ms: number): Promise<void> {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    }

    type B9FixtureScenario = Parameters<
      NonNullable<Window["__amitoneB0Probe"]>["runB9FixtureProof"]
    >[0];

    function buildB9Source(scenario: B9FixtureScenario): ImageData {
      const { width, height } = scenario;
      const data = new Uint8ClampedArray(width * height * 4);
      const cx = width / 2;
      const cy = height / 2;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          let value: number;
          switch (scenario.sourceKind) {
            case "flat":
              value = scenario.tone ?? 128;
              break;
            case "horizontal-gradient":
              value = 235 - (x / Math.max(1, width - 1)) * 200;
              break;
            case "vertical-gradient":
              value = 235 - (y / Math.max(1, height - 1)) * 200;
              break;
            case "line-art":
              value =
                x === y ||
                x + y === width - 1 ||
                x % 37 === 0 ||
                y % 41 === 0
                  ? 0
                  : 166;
              break;
            case "hair-lines":
              value =
                Math.abs(y - height * 0.35 - Math.sin(x / 13) * 8) < 1.2 ||
                Math.abs(y - height * 0.62 - Math.cos(x / 17) * 7) < 1.2
                  ? 0
                  : 188 - (x / Math.max(1, width - 1)) * 90;
              break;
            case "speech-edge": {
              const nx = (x - cx) / (width * 0.33);
              const ny = (y - cy) / (height * 0.26);
              const edge = Math.abs(nx * nx + ny * ny - 1) < 0.045;
              value = edge ? 255 : 122;
              break;
            }
            case "manga-page":
              value = 220 - (((x + y) % 512) / 511) * 150;
              if (x === Math.floor(width * 0.28) || y === Math.floor(height * 0.52)) {
                value = 0;
              } else if (x > width * 0.68 && y > height * 0.64) {
                value = 240;
              }
              break;
          }
          const byte = Math.max(0, Math.min(255, Math.round(value)));
          data[idx] = byte;
          data[idx + 1] = byte;
          data[idx + 2] = byte;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function buildB9LabelMap(
      width: number,
      height: number,
      mode: B9FixtureScenario["labelMode"],
    ): ImageData | undefined {
      if (mode === undefined || mode === "none") return undefined;
      const data = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          let label: number;
          if (mode === "one") {
            label = 1;
          } else if (mode === "three") {
            label = Math.min(3, Math.floor((x * 3) / width) + 1);
          } else if (mode === "four") {
            label = (x < width / 2 ? 0 : 1) + (y < height / 2 ? 0 : 2) + 1;
          } else {
            label = Math.min(8, Math.floor((x * 8) / width) + 1);
          }
          const idx = (y * width + x) * 4;
          data[idx] = label;
          data[idx + 1] = label;
          data[idx + 2] = label;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function b9LabelsForMode(mode: B9FixtureScenario["labelMode"]): number[] {
      if (mode === undefined || mode === "none") return [0];
      if (mode === "one") return [1];
      if (mode === "three") return [1, 2, 3];
      if (mode === "four") return [1, 2, 3, 4];
      return [1, 2, 3, 4, 5, 6, 7, 8];
    }

    function buildB9CustomStamp(): ImageData {
      const size = 16;
      const data = new Uint8ClampedArray(size * size * 4);
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const idx = (y * size + x) * 4;
          const dark =
            x === y ||
            x + y === size - 1 ||
            (x >= 7 && x <= 8) ||
            (y >= 7 && y <= 8);
          const value = dark ? 0 : 255;
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, size, size);
    }

    function buildB9Pattern(): ImageData {
      const width = 32;
      const height = 32;
      const data = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = (y * width + x) * 4;
          const value = (x + y) % 9 < 4 || x === y ? 32 : 224;
          data[idx] = value;
          data[idx + 1] = value;
          data[idx + 2] = value;
          data[idx + 3] = 255;
        }
      }
      return new ImageData(data, width, height);
    }

    function buildB9Method(
      scenario: B9FixtureScenario,
      label: number,
    ): MethodParams {
      const density = Math.min(90, Math.max(10, 50 + (label - 1) * 8));
      const stampType: StampShape["type"] = scenario.stamp ?? "pixel";
      if (scenario.family === "B") {
        return {
          family: "B",
          algorithm: "custom-pattern",
          pattern: buildB9Pattern(),
          isGrayscale: true,
          blurRadius: 0,
          polarity: "black",
          density,
          blendMode: "threshold",
          patternScale: 1,
          patternNoise: 0,
          patternNoiseScale: 1,
          patternInvert: true,
          patternBrightness: 0,
          patternContrast: 0,
          patternOpacity: 1,
          blackThreshold: 0.02,
          whiteThreshold: 0.98,
        };
      }

      const stamp =
        stampType === "custom"
          ? { type: "custom" as const, coverage: 1, mask: buildB9CustomStamp() }
          : ({ type: stampType, coverage: 1 } as StampShape);

      if (scenario.family === "C") {
        return {
          family: "C",
          algorithm: scenario.algorithm ?? "bayer",
          ...(scenario.algorithm === "bluenoise"
            ? {}
            : { matrixSize: 8 as const }),
          cellSize: scenario.cellSize ?? 8,
          stamp,
          polarity: "black",
          density,
          blackThreshold: 0.02,
          whiteThreshold: 0.98,
          gridAngleDeg: 17,
          shapeAngleDeg: stampType === "line" ? 25 : 0,
          algorithmJitter: 0,
          grainStrength: 0,
          grainScale: 1,
        } as MethodParams;
      }

      return {
        family: "A",
        algorithm: scenario.algorithm ?? "bayer",
        ...(scenario.algorithm === "bluenoise" ? {} : { matrixSize: 8 as const }),
        cellSize: scenario.cellSize ?? 8,
        stamp,
        polarity: "black",
        density,
        blackThreshold: 0.02,
        whiteThreshold: 0.98,
        gridAngleDeg: 0,
        shapeAngleDeg: stampType === "line" ? 25 : 0,
        algorithmJitter: scenario.algorithm === "bluenoise" ? 0.075 : 0,
      } as MethodParams;
    }

    function buildB9Regions(scenario: B9FixtureScenario): Array<{
      label: number;
      method: MethodParams;
    }> {
      return b9LabelsForMode(scenario.labelMode).map((label) => ({
        label,
        method: buildB9Method(scenario, label),
      }));
    }

    function computeB9Parity(
      cpu: ImageData,
      gpu: ImageData,
      labelMap: ImageData | undefined,
      cellSize: number,
    ): {
      totalPixels: number;
      flippedPixels: number;
      flipRate: number;
      maxFlipClusterArea: number;
      localDensityMaxDelta16: number;
      localDensityMaxDelta32: number;
      seamBandFlipRate: number;
    } {
      const totalPixels = cpu.width * cpu.height;
      const flipMask = new Uint8Array(totalPixels);
      let flippedPixels = 0;
      for (let pixel = 0; pixel < totalPixels; pixel += 1) {
        const idx = pixel * 4;
        if (cpu.data[idx] !== gpu.data[idx]) {
          flipMask[pixel] = 1;
          flippedPixels += 1;
        }
      }
      return {
        totalPixels,
        flippedPixels,
        flipRate: totalPixels === 0 ? 0 : flippedPixels / totalPixels,
        maxFlipClusterArea: b9MaxClusterArea(flipMask, cpu.width, cpu.height),
        localDensityMaxDelta16: b9LocalDensityDelta(cpu, gpu, 16),
        localDensityMaxDelta32: b9LocalDensityDelta(cpu, gpu, 32),
        seamBandFlipRate: labelMap
          ? b9SeamBandFlipRate(flipMask, labelMap, Math.max(1, Math.round(cellSize * 2)))
          : 0,
      };
    }

    function b9MaxClusterArea(
      mask: Uint8Array,
      width: number,
      height: number,
    ): number {
      const seen = new Uint8Array(mask.length);
      const stack = new Int32Array(mask.length);
      let maxArea = 0;
      for (let start = 0; start < mask.length; start += 1) {
        if (mask[start] === 0 || seen[start] === 1) continue;
        let area = 0;
        let stackSize = 1;
        stack[0] = start;
        seen[start] = 1;
        while (stackSize > 0) {
          const pixel = stack[(stackSize -= 1)]!;
          area += 1;
          const x = pixel % width;
          const y = Math.floor(pixel / width);
          if (x > 0) stackSize = b9PushFlip(pixel - 1, mask, seen, stack, stackSize);
          if (x + 1 < width) stackSize = b9PushFlip(pixel + 1, mask, seen, stack, stackSize);
          if (y > 0) stackSize = b9PushFlip(pixel - width, mask, seen, stack, stackSize);
          if (y + 1 < height) stackSize = b9PushFlip(pixel + width, mask, seen, stack, stackSize);
        }
        if (area > maxArea) maxArea = area;
      }
      return maxArea;
    }

    function b9PushFlip(
      pixel: number,
      mask: Uint8Array,
      seen: Uint8Array,
      stack: Int32Array,
      stackSize: number,
    ): number {
      if (mask[pixel] === 0 || seen[pixel] === 1) return stackSize;
      seen[pixel] = 1;
      stack[stackSize] = pixel;
      return stackSize + 1;
    }

    function b9IsBlack(image: ImageData, pixel: number): boolean {
      return image.data[pixel * 4]! < 128;
    }

    function b9LocalDensityDelta(
      cpu: ImageData,
      gpu: ImageData,
      tileSize: 16 | 32,
    ): number {
      let maxDelta = 0;
      for (let tileY = 0; tileY < cpu.height; tileY += tileSize) {
        for (let tileX = 0; tileX < cpu.width; tileX += tileSize) {
          const tileWidth = Math.min(tileSize, cpu.width - tileX);
          const tileHeight = Math.min(tileSize, cpu.height - tileY);
          const area = tileWidth * tileHeight;
          let cpuBlack = 0;
          let gpuBlack = 0;
          for (let y = tileY; y < tileY + tileHeight; y += 1) {
            for (let x = tileX; x < tileX + tileWidth; x += 1) {
              const pixel = y * cpu.width + x;
              if (b9IsBlack(cpu, pixel)) cpuBlack += 1;
              if (b9IsBlack(gpu, pixel)) gpuBlack += 1;
            }
          }
          const delta = Math.abs(cpuBlack / area - gpuBlack / area);
          if (delta > maxDelta) maxDelta = delta;
        }
      }
      return maxDelta;
    }

    function b9SeamBandFlipRate(
      flipMask: Uint8Array,
      labelMap: ImageData,
      radius: number,
    ): number {
      let seamPixels = 0;
      let seamFlips = 0;
      for (let y = 0; y < labelMap.height; y += 1) {
        for (let x = 0; x < labelMap.width; x += 1) {
          if (!b9NearLabelBoundary(labelMap, x, y, radius)) continue;
          const pixel = y * labelMap.width + x;
          seamPixels += 1;
          if (flipMask[pixel] === 1) seamFlips += 1;
        }
      }
      return seamPixels === 0 ? 0 : seamFlips / seamPixels;
    }

    function b9LabelAt(labelMap: ImageData, x: number, y: number): number {
      return labelMap.data[(y * labelMap.width + x) * 4]!;
    }

    function b9NearLabelBoundary(
      labelMap: ImageData,
      x: number,
      y: number,
      radius: number,
    ): boolean {
      const label = b9LabelAt(labelMap, x, y);
      for (let distance = 1; distance <= radius; distance += 1) {
        if (x - distance >= 0 && b9LabelAt(labelMap, x - distance, y) !== label) return true;
        if (x + distance < labelMap.width && b9LabelAt(labelMap, x + distance, y) !== label) return true;
        if (y - distance >= 0 && b9LabelAt(labelMap, x, y - distance) !== label) return true;
        if (y + distance < labelMap.height && b9LabelAt(labelMap, x, y + distance) !== label) return true;
      }
      return false;
    }

    function imageDataToPngDataUrl(image: ImageData): string {
      const pngCanvas = document.createElement("canvas");
      pngCanvas.width = image.width;
      pngCanvas.height = image.height;
      const ctx = pngCanvas.getContext("2d");
      if (!ctx) throw new Error("2D canvas unavailable for PNG artifact");
      ctx.putImageData(image, 0, 0);
      return pngCanvas.toDataURL("image/png");
    }

    function buildB9DiffImage(cpu: ImageData, gpu: ImageData): ImageData {
      const diff = new ImageData(cpu.width, cpu.height);
      for (let i = 0; i < cpu.data.length; i += 4) {
        const changed = cpu.data[i] !== gpu.data[i];
        diff.data[i] = changed ? 255 : 255;
        diff.data[i + 1] = changed ? 0 : 255;
        diff.data[i + 2] = changed ? 255 : 255;
        diff.data[i + 3] = 255;
      }
      return diff;
    }

    // B0.3: asymmetric 4x4 source for the orientation parity test.
    // CPU ImageData order: row 0 = top. Markers at the four corners so that
    // any upside-down flip, horizontal mirror, or channel swap in the
    // upload/shader/readback pipeline is caught by the gate assertions.
    function buildOrientationSource(): ImageData {
      const data = new Uint8ClampedArray(4 * 4 * 4);
      // Dim grey background everywhere first.
      for (let i = 0; i < data.length; i += 4) {
        data[i] = 80;
        data[i + 1] = 80;
        data[i + 2] = 80;
        data[i + 3] = 255;
      }
      // Four asymmetric corner markers (CPU ImageData order: row 0 = top).
      const setPixel = (x: number, y: number, r: number, g: number, b: number): void => {
        const idx = (y * 4 + x) * 4;
        data[idx] = r; data[idx + 1] = g; data[idx + 2] = b; data[idx + 3] = 255;
      };
      setPixel(0, 0, 255,   0,   0); // TL: red
      setPixel(3, 0,   0, 255,   0); // TR: green
      setPixel(0, 3,   0,   0, 255); // BL: blue
      setPixel(3, 3, 255, 255, 255); // BR: white
      return new ImageData(data, 4, 4);
    }

    window.__amitoneB0Probe = {
      WebGL2DitherBackend,
      createDitherGpuBackend,

      // B0.2 solid-red proof -- kept unchanged per the DO NOT REMOVE mandate.
      async runSolidRedProof() {
        const backend = new WebGL2DitherBackend({ canvas });
        await backend.initialize();

        try {
          const result = await backend.runSolidRedProof();

          return {
            diagnostics: result.diagnostics,
            firstPixelBytes: Array.from(result.output.data.slice(0, 64)),
            width: result.output.width,
            height: result.output.height,
          };
        } finally {
          backend.dispose();
        }
      },

      // B0.3 orientation parity proof -- uses the passthrough shader + asymmetric
      // corner markers to assert that source top-left maps to output top-left.
      // Constructs WebGL2DitherBackend directly (not via createDitherGpuBackend)
      // so runPassthroughProof() -- which is not on the DitherGpuBackend interface --
      // is accessible without a type assertion.
      async runOrientationProof() {
        // Fresh canvas + backend so this proof is independent of runSolidRedProof.
        const orientCanvas = document.createElement("canvas");
        orientCanvas.width = 4;
        orientCanvas.height = 4;

        const backend = new WebGL2DitherBackend({ canvas: orientCanvas });
        await backend.initialize();

        try {
          const source = buildOrientationSource();
          const result = await backend.runPassthroughProof(source);
          return {
            diagnostics: result.diagnostics,
            readbackBytes: Array.from(result.output.data),
            width: result.output.width,
            height: result.output.height,
          };
        } finally {
          backend.dispose();
        }
      },

      // B0.4 cell-average prepass proof.
      // Runs the cell-average prepass shader over a caller-supplied source and
      // returns the avg readback, dimensions, and diagnostics.
      // Does NOT remove or modify runSolidRedProof or runOrientationProof.
      async runCellAverageProof(source: ImageData, cellSize: number) {
        const avgCanvas = document.createElement("canvas");
        avgCanvas.width = source.width;
        avgCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: avgCanvas });
        await backend.initialize();

        try {
          const result = await backend.runCellAverageProof(source, cellSize);
          return {
            diagnostics: result.diagnostics,
            avgBytes: Array.from(result.output.data),
            avgWidth: result.avgWidth,
            avgHeight: result.avgHeight,
            sourceWidth: source.width,
            sourceHeight: source.height,
            cellSize,
          };
        } finally {
          backend.dispose();
        }
      },

      // B0.5 Family A Bayer pixel proof.
      // Two-pass GPU path: cell-average prepass + Bayer threshold.
      // Returns the full-resolution binary output and diagnostics.
      // Does NOT remove or modify the existing four probes above.
      async runFamilyABayerPixelProof(
        source: ImageData,
        params: {
          cellSize: number;
          matrixSize: 2 | 4 | 8;
          polarity: "black" | "white";
          density: number;
        },
      ) {
        const bayerCanvas = document.createElement("canvas");
        bayerCanvas.width = source.width;
        bayerCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: bayerCanvas });
        await backend.initialize();

        try {
          const result = await backend.runFamilyABayerPixelProof(source, params);
          return {
            diagnostics: result.diagnostics,
            outputBytes: Array.from(result.output.data),
            width: source.width,
            height: source.height,
          };
        } finally {
          backend.dispose();
        }
      },

      async runFamilyAOrderedProof(
        source: ImageData,
        method: WebGL2FamilyAOrderedMethod,
      ) {
        const familyACanvas = document.createElement("canvas");
        familyACanvas.width = source.width;
        familyACanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: familyACanvas });
        await backend.initialize();

        try {
          const result = await backend.runFamilyAOrderedProof(source, method);
          return {
            diagnostics: result.diagnostics,
            outputBytes: Array.from(result.output.data),
            width: source.width,
            height: source.height,
          };
        } finally {
          backend.dispose();
        }
      },

      async runFamilyBPatternProof(
        source: ImageData,
        method: WebGL2FamilyBPatternMethod,
      ) {
        const familyBCanvas = document.createElement("canvas");
        familyBCanvas.width = source.width;
        familyBCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: familyBCanvas });
        await backend.initialize();

        try {
          const [gpuResult, cpuResult] = await Promise.all([
            backend.runFamilyBPatternProof(source, method),
            coreDither({ source, regions: [{ label: 0, method }] }),
          ]);
          return {
            diagnostics: gpuResult.diagnostics,
            outputBytes: Array.from(gpuResult.output.data),
            cpuBytes: Array.from(cpuResult.output.data),
            width: source.width,
            height: source.height,
          };
        } finally {
          backend.dispose();
        }
      },

      async runFamilyCTransformProof(
        source: ImageData,
        method: WebGL2FamilyCTransformMethod,
      ) {
        const familyCCanvas = document.createElement("canvas");
        familyCCanvas.width = source.width;
        familyCCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: familyCCanvas });
        await backend.initialize();

        try {
          const [gpuResult, cpuResult] = await Promise.all([
            backend.runFamilyCTransformProof(source, method),
            coreDither({ source, regions: [{ label: 0, method }] }),
          ]);
          return {
            diagnostics: gpuResult.diagnostics,
            outputBytes: Array.from(gpuResult.output.data),
            cpuBytes: Array.from(cpuResult.output.data),
            width: source.width,
            height: source.height,
          };
        } finally {
          backend.dispose();
        }
      },

      async runFamilyCTransformMetrics(
        source: ImageData,
        method: WebGL2FamilyCTransformMethod,
      ) {
        const familyCCanvas = document.createElement("canvas");
        familyCCanvas.width = source.width;
        familyCCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: familyCCanvas });
        await backend.initialize();

        try {
          const capture: { diagnostics: GpuRenderDiagnostics | null } = {
            diagnostics: null,
          };
          const observedBackend: DitherGpuBackend = {
            name: backend.name,
            initialize: () => backend.initialize(),
            isReady: () => backend.isReady(),
            getCaps: () => backend.getCaps(),
            renderRegionBatch: async (req) => {
              const result = await backend.renderRegionBatch(req);
              capture.diagnostics = result.diagnostics;
              return result;
            },
            dispose: () => backend.dispose(),
          };
          const [gpuResult, cpuResult] = await Promise.all([
            coreDither(
              { source, regions: [{ label: 0, method }] },
              { backend: observedBackend },
            ),
            coreDither({ source, regions: [{ label: 0, method }] }),
          ]);
          const diagnostics = capture.diagnostics;
          if (!diagnostics) {
            throw new Error("Family C pipeline proof did not capture GPU diagnostics");
          }
          const gpu = gpuResult.output.data;
          const cpu = cpuResult.output.data;
          let diffPixels = 0;
          let maxChannelDelta = 0;
          let gpuBlackPixels = 0;
          let cpuBlackPixels = 0;
          let gpuGrayPixels = 0;
          let cpuGrayPixels = 0;
          for (let i = 0; i < gpu.length; i += 4) {
            const dr = Math.abs((gpu[i] ?? 0) - (cpu[i] ?? 0));
            const dg = Math.abs((gpu[i + 1] ?? 0) - (cpu[i + 1] ?? 0));
            const db = Math.abs((gpu[i + 2] ?? 0) - (cpu[i + 2] ?? 0));
            const delta = Math.max(dr, dg, db);
            if (delta > 0) diffPixels += 1;
            if (delta > maxChannelDelta) maxChannelDelta = delta;
            const gpuValue = gpu[i] ?? 255;
            const cpuValue = cpu[i] ?? 255;
            if (gpuValue < 128) gpuBlackPixels += 1;
            if (cpuValue < 128) cpuBlackPixels += 1;
            if (gpuValue > 0 && gpuValue < 255) gpuGrayPixels += 1;
            if (cpuValue > 0 && cpuValue < 255) cpuGrayPixels += 1;
          }
          return {
            diagnostics,
            width: source.width,
            height: source.height,
            diffPixels,
            flipRate: diffPixels / (source.width * source.height),
            maxChannelDelta,
            gpuBlackPixels,
            cpuBlackPixels,
            gpuGrayPixels,
            cpuGrayPixels,
          };
        } finally {
          backend.dispose();
        }
      },

      async runDemoPipelineProof() {
        const source = buildBayerPipelineSource();
        const sourceChecksumBefore = checksumBytes(source.data);
        const heapBefore = usedHeapSize();
        const pipelineCanvas = document.createElement("canvas");
        pipelineCanvas.width = source.width;
        pipelineCanvas.height = source.height;
        const backend = await createDitherGpuBackend(
          { kind: "webgl2", canvas: pipelineCanvas },
          { preferred: "webgl2", allowFallback: false },
        );
        if (!backend) throw new Error("createDitherGpuBackend returned null");

        const capture: { diagnostics: GpuRenderDiagnostics | null } = {
          diagnostics: null,
        };
        const observedBackend: DitherGpuBackend = {
          name: backend.name,
          initialize: () => backend.initialize(),
          isReady: () => backend.isReady(),
          getCaps: () => backend.getCaps(),
          renderRegionBatch: async (req) => {
            const result = await backend.renderRegionBatch(req);
            capture.diagnostics = result.diagnostics;
            return result;
          },
          dispose: () => backend.dispose(),
        };

        const method: MethodParams = {
          family: "A",
          algorithm: "bayer",
          matrixSize: 2,
          cellSize: 2,
          stamp: { type: "pixel", coverage: 1 },
          polarity: "black",
          density: 50,
          blackThreshold: 0,
          whiteThreshold: 1,
          gridAngleDeg: 0,
          shapeAngleDeg: 0,
          algorithmJitter: 1,
        };

        try {
          const result = await coreDither(
            {
              source,
              regions: [{ label: 0, method }],
            },
            { backend: observedBackend },
          );
          const diagnostics = capture.diagnostics;
          if (!diagnostics) {
            throw new Error("demo pipeline proof did not capture GPU diagnostics");
          }
          const heapAfter = usedHeapSize();
          const heapDelta =
            heapBefore !== null && heapAfter !== null ? heapAfter - heapBefore : null;
          return {
            diagnostics,
            outputBytes: Array.from(result.output.data),
            width: result.output.width,
            height: result.output.height,
            textureBytes: diagnostics.textureBytes ?? 0,
            heapBefore,
            heapAfter,
            heapDelta,
            sourceChecksumBefore,
            sourceChecksumAfter: checksumBytes(source.data),
          };
        } finally {
          backend.dispose();
        }
      },

      async runRegionBatchProof() {
        const source = buildRegionBatchSource();
        const labelMap = buildRegionBatchLabelMap();
        const regions = buildRegionBatchRegions();
        const batchCanvas = document.createElement("canvas");
        batchCanvas.width = source.width;
        batchCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: batchCanvas });
        await backend.initialize();

        try {
          const result = await backend.runFamilyABayerPixelRegionBatchProof(
            source,
            labelMap,
            regions,
          );
          return {
            diagnostics: result.diagnostics,
            outputBytes: Array.from(result.output.data),
            width: result.output.width,
            height: result.output.height,
            textureBytes: result.diagnostics.textureBytes ?? 0,
          };
        } finally {
          backend.dispose();
        }
      },

      async runPerRegionReadbackBenchmarkProof(scenario = {}) {
        const width = scenario.width ?? 8;
        const height = scenario.height ?? 8;
        const regionCount = scenario.regionCount ?? 2;
        const source =
          width === 8 && height === 8 && regionCount === 2
            ? buildRegionBatchSource()
            : buildBenchmarkSource(width, height);
        const labelMap =
          width === 8 && height === 8 && regionCount === 2
            ? buildRegionBatchLabelMap()
            : buildBenchmarkLabelMap(width, height, regionCount);
        const regions =
          width === 8 && height === 8 && regionCount === 2
            ? buildRegionBatchRegions()
            : buildBenchmarkRegions(width, regionCount);
        const benchCanvas = document.createElement("canvas");
        benchCanvas.width = source.width;
        benchCanvas.height = source.height;

        const backend = new WebGL2DitherBackend({ canvas: benchCanvas });
        await backend.initialize();

        try {
          const result = await backend.runPerRegionReadbackBenchmark(
            source,
            labelMap,
            regions,
          );
          return {
            diagnostics: result.diagnostics,
            perRegionReadbackMs: result.perRegionReadbackMs,
            width: result.output.width,
            height: result.output.height,
            regionCount,
          };
        } finally {
          backend.dispose();
        }
      },

      async runB8ValidationProof(scenario = {}) {
        const repeatedCycles = scenario.repeatedCycles ?? 10;
        const largeFixtures = scenario.largeFixtures ?? [
          { name: "large-3000x4000", width: 3000, height: 4000 },
          { name: "large-4096x2160", width: 4096, height: 2160 },
        ];

        const capsCanvas = document.createElement("canvas");
        capsCanvas.width = 1;
        capsCanvas.height = 1;
        const capsBackend = new WebGL2DitherBackend({ canvas: capsCanvas });
        let caps: GpuCaps | null = null;
        let capsAvailable = false;
        try {
          await capsBackend.initialize();
          caps = capsBackend.getCaps();
          capsAvailable = capsBackend.isReady();
        } finally {
          capsBackend.dispose();
        }
        const diagnosticsPayload = createGpuDiagnosticsPayload({
          userAgent: navigator.userAgent,
          isIpadLike: isIpadLike(),
          gpuEnabled: true,
          available: capsAvailable,
          caps,
          contextLoss: {
            contextLossCount: 0,
            lastContextLossTimestamp: null,
          },
          currentCapabilitySet: ["webgl2", "b8-ipad-validation"],
          lastShaderCompileError: null,
        });

        const contextCanvas = document.createElement("canvas");
        contextCanvas.width = 8;
        contextCanvas.height = 8;
        let contextLossEvents = 0;
        contextCanvas.addEventListener("webglcontextlost", (event) => {
          event.preventDefault();
          contextLossEvents += 1;
        });
        const contextBackend = new WebGL2DitherBackend({
          canvas: contextCanvas,
        });
        await contextBackend.initialize();
        const contextSource = buildBayerPipelineSource();
        let contextLoss = {
          extensionAvailable: false,
          beforeReady: contextBackend.isReady(),
          afterReady: contextBackend.isReady(),
          contextLost: false,
          contextLossEvents,
          fallbackDiagnostics: null as GpuRenderDiagnostics | null,
          outputChecksum: 0,
          error: null as string | null,
        };
        try {
          await runB8DitherFrame(contextSource, contextBackend);
          const gl = contextCanvas.getContext(
            "webgl2",
          ) as WebGL2RenderingContext | null;
          const loseContext = gl?.getExtension("WEBGL_lose_context") ?? null;
          contextLoss.extensionAvailable = loseContext !== null;
          if (!gl || !loseContext) {
            throw new Error("WEBGL_lose_context unavailable");
          }
          loseContext.loseContext();
          const lossStart = performance.now();
          while (!gl.isContextLost() && performance.now() - lossStart < 1000) {
            await sleep(25);
          }
          const fallbackFrame = await runB8DitherFrame(
            contextSource,
            contextBackend,
          );
          contextLoss = {
            extensionAvailable: true,
            beforeReady: true,
            afterReady: contextBackend.isReady(),
            contextLost: gl.isContextLost(),
            contextLossEvents,
            fallbackDiagnostics: fallbackFrame.diagnostics,
            outputChecksum: fallbackFrame.outputChecksum,
            error: null,
          };
          loseContext.restoreContext();
          await sleep(50);
        } catch (err) {
          contextLoss = {
            ...contextLoss,
            afterReady: contextBackend.isReady(),
            contextLossEvents,
            error: err instanceof Error ? err.message : String(err),
          };
        } finally {
          contextBackend.dispose();
        }

        const repeatedSource = buildB8ValidationSource(256, 256);
        const repeatedChecksumBefore = checksumBytes(repeatedSource.data);
        const repeatedCanvas = document.createElement("canvas");
        repeatedCanvas.width = repeatedSource.width;
        repeatedCanvas.height = repeatedSource.height;
        const repeatedBackend = new WebGL2DitherBackend({
          canvas: repeatedCanvas,
        });
        await repeatedBackend.initialize();
        const repeatedReadbackCounts: number[] = [];
        let repeatedGpuFrames = 0;
        let repeatedFallbackFrames = 0;
        let repeatedTextureBytesMax = 0;
        const repeatedStart = performance.now();
        try {
          for (let i = 0; i < repeatedCycles; i += 1) {
            const frame = await runB8DitherFrame(
              repeatedSource,
              repeatedBackend,
            );
            const diagnostics = frame.diagnostics;
            if (diagnostics?.backend === "gpu-webgl2") {
              repeatedGpuFrames += 1;
            } else {
              repeatedFallbackFrames += 1;
            }
            repeatedReadbackCounts.push(diagnostics?.readbackCount ?? 0);
            repeatedTextureBytesMax = Math.max(
              repeatedTextureBytesMax,
              diagnostics?.textureBytes ?? 0,
            );
          }
        } finally {
          repeatedBackend.dispose();
        }

        const visibilitySource = buildB8ValidationSource(128, 128);
        const visibilityCanvas = document.createElement("canvas");
        visibilityCanvas.width = visibilitySource.width;
        visibilityCanvas.height = visibilitySource.height;
        const visibilityBackend = new WebGL2DitherBackend({
          canvas: visibilityCanvas,
        });
        await visibilityBackend.initialize();
        let visibilityFrame: Awaited<
          ReturnType<typeof runB8DitherFrame>
        > | null = null;
        try {
          document.dispatchEvent(new Event("visibilitychange"));
          document.dispatchEvent(new Event("visibilitychange"));
          visibilityFrame = await runB8DitherFrame(
            visibilitySource,
            visibilityBackend,
          );
        } finally {
          visibilityBackend.dispose();
        }

        const largeResults = [];
        for (const fixture of largeFixtures) {
          const source = buildB8ValidationSource(fixture.width, fixture.height);
          const fixtureCanvas = document.createElement("canvas");
          fixtureCanvas.width = source.width;
          fixtureCanvas.height = source.height;
          const fixtureBackend = new WebGL2DitherBackend({
            canvas: fixtureCanvas,
          });
          await fixtureBackend.initialize();
          try {
            const frame = await runB8DitherFrame(source, fixtureBackend);
            if (!frame.diagnostics) {
              throw new Error(`${fixture.name} did not produce diagnostics`);
            }
            largeResults.push({
              name: fixture.name,
              width: source.width,
              height: source.height,
              diagnostics: frame.diagnostics,
              outputChecksum: frame.outputChecksum,
              totalMs: frame.diagnostics.totalMs ?? 0,
              textureBytes: frame.diagnostics.textureBytes ?? 0,
            });
          } finally {
            fixtureBackend.dispose();
          }
        }

        return {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          maxTouchPoints: navigator.maxTouchPoints,
          isIpadLike: isIpadLike(),
          caps,
          diagnosticsPayload,
          diagnosticsJson: JSON.stringify(diagnosticsPayload, null, 2),
          contextLoss,
          repeatedRender: {
            cycles: repeatedCycles,
            gpuFrames: repeatedGpuFrames,
            fallbackFrames: repeatedFallbackFrames,
            readbackCounts: repeatedReadbackCounts,
            totalMs: performance.now() - repeatedStart,
            textureBytesMax: repeatedTextureBytesMax,
            sourceChecksumBefore: repeatedChecksumBefore,
            sourceChecksumAfter: checksumBytes(repeatedSource.data),
          },
          visibilityCycle: {
            dispatchCount: 2,
            diagnostics: visibilityFrame?.diagnostics ?? null,
            outputChecksum: visibilityFrame?.outputChecksum ?? 0,
          },
          largeFixtures: largeResults,
        };
      },

      async runB9FixtureProof(scenario) {
        const source = buildB9Source(scenario);
        const labelMap = buildB9LabelMap(
          source.width,
          source.height,
          scenario.labelMode,
        );
        const regions = buildB9Regions(scenario);
        const canvas = document.createElement("canvas");
        canvas.width = source.width;
        canvas.height = source.height;
        const backend = new WebGL2DitherBackend({ canvas });
        await backend.initialize();
        const observed = observeBackend(backend);
        const fallbackDiagnostics: GpuRenderDiagnostics[] = [];
        const input: DitherInput = {
          source,
          ...(labelMap !== undefined ? { labelMap } : {}),
          regions,
        };

        const cpuStart = performance.now();
        try {
          const cpuResult = await coreDither(input);
          const cpuMs = performance.now() - cpuStart;
          const gpuStart = performance.now();
          const gpuResult = await coreDither(input, {
            backend: observed.backend,
            onGpuFallback: (diagnostics) => {
              fallbackDiagnostics.push(diagnostics);
            },
          });
          const gpuMs = performance.now() - gpuStart;

          const diagnostics =
            observed.capture.diagnostics ??
            fallbackDiagnostics[fallbackDiagnostics.length - 1] ??
            null;
          const metrics = computeB9Parity(
            cpuResult.output,
            gpuResult.output,
            labelMap,
            scenario.cellSize ?? 8,
          );
          return {
            name: scenario.name,
            width: source.width,
            height: source.height,
            family: scenario.family,
            backend: diagnostics?.backend ?? "missing",
            diagnostics,
            metrics,
            cpuMs,
            gpuMs,
            contextLossCount: diagnostics?.contextLost ? 1 : 0,
            cpuPng: imageDataToPngDataUrl(cpuResult.output),
            gpuPng: imageDataToPngDataUrl(gpuResult.output),
            diffPng: imageDataToPngDataUrl(
              buildB9DiffImage(cpuResult.output, gpuResult.output),
            ),
          };
        } finally {
          backend.dispose();
        }
      },
    };
    document.body.dataset["amitoneB0ProbeReady"] = "true";
  })();
}

void main();
